import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { useHistory, useLocation } from "react-router-dom";
import { uniqBy } from "lodash";

import { AjaxForm } from "./index.jsx";
import { Field, getIn, useFormikContext } from "formik";
import { useAlert } from "react-alert";
import { PdfShower } from "../components";
import { WARRANTY_DATE_PLACEHOLDER } from "../core/warrantyDate";
import { ch24PublishPresentation } from "../helpers/chrono24PublishState";
import { gbpToAccountPrice } from "../helpers/chrono24PriceFx";
import { WF_COMMISSION, WF_FEE_GBP, storePriceGbp, wfPayout, wfPriceForPayout } from "../helpers/wfPayout";
import { UserSearchSelect, DateInput } from "../components/inputs";
import { userOptionName } from "../components/inputs/UserSearchSelect";
import useUserUpdates from "../hooks/useUserUpdates";
import { showApiError } from "../hooks/useApiError.js";
import { ProductImages } from "./Product/index.jsx";
import { FolderList } from "../views/Listing/index";
import { getFileUrl, getApiUrl } from "../core/config/api.js";
import { shrinkImageForUpload } from "../core/utils/shrinkImage.js";
import { wfFitAndPadRect, wfCoverRect, wfResizeToHeightSize, wfUploadBlob } from "../helpers/wfImageAutoFix.js";
import { ListingHelper, DataHelper, ProductHelper } from "../helpers";
import { SettingsContext, UserContext, Window, useEntitlements } from "../contexts/index.jsx";
import { LockedTabPanel, LockDot } from "../components/LockedTabPanel";
import { PageLayout, PageHeader } from "../components/layout";
import { Button, ButtonGroup, ConfirmDialog, Icon, IconButton, Select, Tabs, Toggle } from "../components/ui";
import SectionTrigger from "./SectionTrigger.jsx";
import WfFixChoiceModal from "./WfFixChoiceModal.jsx";
import { boxOptions, conditionOptions, yearOptions } from "../product/constants";
import { ownedByOptions } from "../listings/constants";
import { SupplierWizardModal } from "../views/Users";
import RCProductSelector from "../product/components/RCProductSelector";
import { useChrono24Accounts, useListedChrono24Accounts, useListingMarketplaceState, useMarketplaceAccounts } from "../hooks/marketplaceAccounts";
import { useAllFx } from "../hooks/fx";
import { useActiveStoreIdM } from "../core/hooks/useActiveStoreIdM";
import { baseSymbol, symbolFor, getBaseCurrency, currencyOptions, currencyMeta, isMulticurrency } from "../core/currency";
// Constant only — intakeApi pulls in nothing from this file, so this import does
// not close the ESM cycle the renderContracts note below is about.
import { IN_REPAIR_MARKER } from "../listingFlows/intakeApi";
import { AutoSaveStatusContext, SectionSavedBadge } from "./autoSaveStatus";
import { repairAgeSummary } from "../core/repair";
import PriceCheckTabBar from "../listings/priceCheck/PriceCheckTabBar";
import "./rc-listing-form.scss";

// ─── Path helper (bracket → dot for Formik getIn) ─────────────────────────────
const fget = (values, name) =>
  getIn(values, name.replace(/\[([^\]]+)\]/g, ".$1")) ?? "";

// The legacy "[In Repair]" note marker, as a case-insensitive matcher. Built by
// escaping the constant rather than hand-writing a pattern so the two can never
// drift apart (the marker is bracketed, i.e. all regex metacharacters).
const IN_REPAIR_MARKER_RE = new RegExp(
  IN_REPAIR_MARKER.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
  "gi"
);

// ─── Icons ────────────────────────────────────────────────────────────────────
// Stacked layers — the same identity the sidebar's Listings item uses, so the
// section reads as "the marketplace listings" rather than another lines-of-text
// glyph next to Internal Details.
const IList = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M8 2.5L14 5.5L8 8.5L2 5.5L8 2.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
    <path d="M2 10.5L8 13.5L14 10.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IImage = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <rect x="2" y="3" width="12" height="10" rx="2" stroke="currentColor" strokeWidth="1.3"/>
    <circle cx="5.5" cy="6.5" r="1" fill="currentColor"/>
    <path d="M2 10.5L5 7.5L7.5 10L10 8L14 11.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IDetails = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M3 4h10M3 8h10M3 12h6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);
const IPayment = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <rect x="2" y="4" width="12" height="9" rx="2" stroke="currentColor" strokeWidth="1.3"/>
    <line x1="2" y1="7.5" x2="14" y2="7.5" stroke="currentColor" strokeWidth="1.3"/>
    <rect x="4" y="9.5" width="3" height="1.5" rx="0.5" fill="currentColor"/>
  </svg>
);
const IUpload = () => (
  <svg width="36" height="36" viewBox="0 0 32 32" fill="none">
    <path d="M16 22V12M16 12L12 16M16 12L20 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8 24H24" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);
const IChevronRight = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M4.5 3L7.5 6L4.5 9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IChevronDown = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ─── Custom field primitives ──────────────────────────────────────────────────

/**
 * Fully custom pill dropdown — no native <select>, fully styled to RC OS v3.
 * Opens a dark floating panel with hover + selected states.
 */
// Superscript asterisk marking a mandatory field. `required` on a field
// primitive must mirror ListingHelper's validation schema (ListingFields.
// required) — the marker is documentation of the gate, not the gate itself.
const RCRequiredMark = () => <sup className="rc-field__req" aria-label="required">*</sup>;

/**
 * Formik-bound ui Select. Options arrive in either the {id, name} or the
 * {value, label} shape; the picked option's raw id is written back unchanged
 * (number stays number), exactly as the old pill dropdown did. The stored
 * value is matched by String() so "1" and 1 select the same option.
 */
const toSelectOption = (o) => ({ value: o.id ?? o.value, label: String(o.name ?? o.label ?? "") });

const RCSelect = ({ label, name, options = [], readOnly = false, required = false }) => {
  const { values, setFieldValue } = useFormikContext();
  const val = fget(values, name);
  const items = useMemo(() => options.map(toSelectOption), [options]);
  const selected = items.find((o) => String(o.value) === String(val));

  return (
    <div className="rc-field">
      <div className="rc-field__label">{label}{required && <RCRequiredMark />}</div>
      <Select
        size="lg"
        fullWidth
        label={label}
        placeholder="—"
        options={items}
        value={selected ? selected.value : null}
        disabled={readOnly}
        onChange={(v) => setFieldValue(name, v)}
      />
    </div>
  );
};

/**
 * Searchable Formik-bound ui Select — the menu searches the backend through
 * `loadOptions(query)` (WatchFinderCategoryController@search) instead of
 * listing every option. `selectedOption` ({id, name}) labels the closed
 * control, since the search results only ever hold the current query's
 * matches — the selected item may not be among them.
 */
const RCSearchSelect = ({ label, name, selectedOption, loadOptions, placeholder = "Search...", onPick }) => {
  const { setFieldValue } = useFormikContext();
  const known = useMemo(
    () => (selectedOption ? [{ value: selectedOption.id, label: String(selectedOption.name), raw: selectedOption }] : []),
    [selectedOption]
  );
  const search = useCallback(
    async (q) => {
      try {
        const opts = (await loadOptions(q)) || [];
        return opts.map((o) => ({ value: o.id, label: String(o.name), raw: o }));
      } catch {
        return [];
      }
    },
    [loadOptions]
  );

  return (
    <div className="rc-field">
      <div className="rc-field__label">{label}</div>
      <Select
        size="lg"
        fullWidth
        label={label}
        placeholder="—"
        searchPlaceholder={placeholder}
        options={known}
        loadOptions={search}
        value={selectedOption ? selectedOption.id : null}
        onChange={(v, opt) => {
          setFieldValue(name, v);
          onPick?.(opt?.raw ?? { id: v, name: opt?.label });
        }}
      />
    </div>
  );
};

/** Pill text input */
const RCInput = ({ label, name, placeholder, readOnly = false, required = false }) => {
  const { values, setFieldValue } = useFormikContext();
  const val = fget(values, name);
  const displayLabel = label || placeholder;
  return (
    <div className="rc-field">
      {displayLabel && <div className="rc-field__label">{displayLabel}{required && <RCRequiredMark />}</div>}
      <div className="rc-field__ctrl">
        <input
          type="text"
          className="rc-field__input"
          value={val ?? ""}
          placeholder={placeholder}
          readOnly={readOnly}
          onChange={(e) => !readOnly && setFieldValue(name, e.target.value)}
        />
      </div>
    </div>
  );
};

/** Pill number input */
const RCNumber = ({ label, name, placeholder }) => {
  const { values, setFieldValue } = useFormikContext();
  const val = fget(values, name);
  return (
    <div className="rc-field">
      <div className="rc-field__label">{label || placeholder}</div>
      <div className="rc-field__ctrl">
        <input
          type="number"
          className="rc-field__input"
          value={val ?? ""}
          placeholder={placeholder}
          onChange={(e) => setFieldValue(name, e.target.value)}
        />
      </div>
    </div>
  );
};

/** Date field — uses the shared RC DateInput (react-datepicker), the same
 *  calendar used elsewhere in the app (e.g. PAYMENTS). Stored as YYYY-MM-DD
 *  to match the backend date cast. RC-themed input box via .rc-lp__date. */
const RCDate = ({ label, name, placeholder = "Select date..." }) => {
  const { values, setFieldValue } = useFormikContext();
  const val = fget(values, name);
  return (
    <div className="rc-field">
      {label && <div className="rc-field__label">{label}</div>}
      <div className="rc-field__ctrl rc-lp__date">
        <DateInput
          name={name}
          value={val || null}
          placeholder={placeholder}
          dateFormat="MMMM d, yyyy"
          popperClassName="rc-dark-calendar"
          onChange={(d) => {
            const str = d
              ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
              : null;
            setFieldValue(name, str);
          }}
        />
      </div>
    </div>
  );
};

/** Invoice uploader — accepts images AND PDFs (the RUG gallery used for
 *  Listing Images is image-only). Uploads to /api/files and stores
 *  [{ id, name }] in Formik; images preview as thumbnails, PDFs as a chip,
 *  both open in a new tab. Styled to sit alongside the other RC fields. */
const fileKey = (item) => String(item?.id ?? item);

const InvoiceUpload = ({ name }) => {
  const { values, setFieldValue } = useFormikContext();
  // One entry per file id. A repeat (from the server, or an upload racing a
  // re-render) would otherwise be saved back by auto-save as a second row.
  const items = uniqBy(fget(values, name) || [], fileKey);
  // The upload awaits the network; read the list as it is THEN, not as it
  // was when the picker opened, so a concurrent change is not overwritten.
  const liveValues = useRef(values);
  liveValues.current = values;
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const isPdf = (item) => (item?.name || "").toLowerCase().endsWith(".pdf");

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setError("");
    setUploading(true);
    try {
      const added = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", await shrinkImageForUpload(f));
        const { data } = await axios.post(getApiUrl("/api/files"), fd);
        if (data?.id) added.push({ id: data.id, name: data.original_name || f.name });
      }
      if (added.length) {
        const current = fget(liveValues.current, name) || [];
        setFieldValue(name, uniqBy([...current, ...added], fileKey));
      }
    } catch (err) {
      setError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeItem = (id) =>
    setFieldValue(name, items.filter((i) => (i.id ?? i) !== id));

  return (
    <div className="rc-lp__invoices">
      <div className="rc-lp__invoices-grid">
        {items.map((item) => {
          const id = item.id ?? item;
          const pdf = isPdf(item);
          // Invoices are gated server-side; prefer the backend's pre-signed
          // URLs (item.pdf_url / item.file_url), falling back to the bare URL.
          const href = pdf
            ? (item.pdf_url ?? getApiUrl(`/api/pdfs/${id}`))
            : (item.file_url ?? getFileUrl(id));
          return (
            <div key={id} className="rc-lp__invoice-card">
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="rc-lp__invoice-thumb"
              >
                {pdf ? (
                  <span className="rc-lp__invoice-pdf">PDF</span>
                ) : (
                  <img src={item.file_url ?? getFileUrl(id)} alt={item.name || "invoice"} />
                )}
              </a>
              <IconButton
                size="sm"
                round
                className="rc-lp__invoice-remove"
                label="Remove invoice"
                data-btn="invoice-delete"
                onClick={() => removeItem(id)}
              >
                <Icon name="X" size="sm" />
              </IconButton>
              {item.name && <div className="rc-lp__invoice-name">{item.name}</div>}
            </div>
          );
        })}
      </div>
      <Button
        variant="secondary"
        data-btn="invoice-add"
        leadingIcon={<Icon name="Upload" size="sm" />}
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
      >
        {uploading ? "Uploading…" : "Add image or PDF"}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        multiple
        hidden
        onChange={onPick}
      />
      {error && <div className="rc-lp__invoice-error">{error}</div>}
    </div>
  );
};

/** Pill price input — reads currency from a sibling Formik field, formats with symbol + commas */
const RCCurrencyInput = ({ label, name, currencyField, placeholder = "Enter price..." }) => {
  const { values, setFieldValue } = useFormikContext();
  const rawVal = fget(values, name);
  const currency = fget(values, currencyField) || getBaseCurrency();
  // Shared symbol table — no local map.
  const symbol = symbolFor(currency);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef(null);

  const formatted = (() => {
    const n = parseFloat(String(rawVal).replace(/[£€$₮,\s]/g, ""));
    if (!rawVal || rawVal === 0 || rawVal === "0" || isNaN(n)) return "";
    return `${symbol}${n.toLocaleString("en-GB", { maximumFractionDigits: 2 })}`;
  })();

  const startEdit = () => {
    const n = parseFloat(String(rawVal).replace(/[£€$₮,\s]/g, ""));
    setDraft(!rawVal || isNaN(n) ? "" : String(n));
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const commit = () => {
    const n = parseFloat(draft.replace(/[£€$₮,\s]/g, ""));
    setFieldValue(name, isNaN(n) ? 0 : n);
    setEditing(false);
  };

  return (
    <div className="rc-field">
      {label && <div className="rc-field__label">{label}</div>}
      {editing ? (
        <div className="rc-field__ctrl rc-field__ctrl--currency">
          <span className="rc-field__currency-symbol">{symbol}</span>
          <input
            ref={inputRef}
            type="text"
            className="rc-field__input rc-field__input--money rc-field__input--has-prefix"
            value={draft}
            placeholder="0"
            onChange={(e) => setDraft(e.target.value.replace(/[£€$₮,\s]/g, ""))}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
          />
        </div>
      ) : (
        <button type="button" className="rc-field__pill" onClick={startEdit}>
          <span className={`rc-field__pill-value${!formatted ? " rc-field__pill-value--empty" : ""}`}>
            {formatted || placeholder}
          </span>
          <span className="rc-field__chevron"><IChevronDown /></span>
        </button>
      )}
    </div>
  );
};

/** Pill money input — shows formatted value, editable on click.
 *  Defaults to the store base-currency symbol (legacy £ until multi-currency is
 *  seeded); per-account Chrono24 fields pass an explicit `currencySymbol`
 *  (EUR, AED, etc.) that overrides it. `currencyField` binds the symbol live to
 *  a sibling Formik currency field (e.g. the store price + its currency dropdown). */
const RCMoney = ({ label, name, placeholder = "Price on request", readOnly = false, currencySymbol, currencyField }) => {
  const { values, setFieldValue } = useFormikContext();
  const rawVal = fget(values, name);
  // Symbol precedence: explicit prop → sibling currency field → store base.
  const effectiveSymbol = currencySymbol
    ?? (currencyField ? symbolFor(fget(values, currencyField) || getBaseCurrency()) : baseSymbol());
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef(null);

  // Strip both the configured symbol and any common ones the user might paste in.
  const stripSymbols = (s) => String(s).replace(/[£€$₮,\s]/g, "");

  const formatted = (() => {
    const n = parseFloat(stripSymbols(rawVal));
    return !rawVal || rawVal === 0 || rawVal === "0" ? "" : isNaN(n) ? "" : `${effectiveSymbol}${n.toLocaleString("en-GB")}`;
  })();

  const startEdit = () => {
    if (readOnly) return;
    const n = parseFloat(stripSymbols(rawVal));
    setDraft(!rawVal || rawVal === 0 ? "" : isNaN(n) ? "" : String(n));
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const commitEdit = () => {
    const n = parseFloat(stripSymbols(draft));
    setFieldValue(name, isNaN(n) ? 0 : n);
    setEditing(false);
  };

  return (
    <div className="rc-field">
      <div className="rc-field__label">{label || placeholder}</div>
      {editing ? (
        <div className="rc-field__ctrl">
          <input
            ref={inputRef}
            type="text"
            className="rc-field__input rc-field__input--money"
            value={draft}
            placeholder="0"
            onChange={(e) => setDraft(stripSymbols(e.target.value))}
            onBlur={commitEdit}
            onKeyDown={(e) => { if (e.key === "Enter") commitEdit(); if (e.key === "Escape") setEditing(false); }}
          />
        </div>
      ) : (
        <button type="button" className="rc-field__pill" disabled={readOnly} onClick={startEdit}>
          <span className={`rc-field__pill-value${!formatted ? " rc-field__pill-value--empty" : ""}`}>
            {formatted || placeholder}
          </span>
          <span className="rc-field__chevron"><IChevronDown /></span>
        </button>
      )}
    </div>
  );
};

/** Pill textarea */
const RCTextarea = ({ label, name, placeholder }) => {
  const { values, setFieldValue } = useFormikContext();
  const val = fget(values, name);
  return (
    <div className="rc-field rc-field--full">
      <div className="rc-field__label">{label || placeholder}</div>
      <textarea
        className="rc-field__textarea"
        value={val ?? ""}
        placeholder={placeholder}
        onChange={(e) => setFieldValue(name, e.target.value)}
      />
    </div>
  );
};

/**
 * Formik-bound ui Toggle — writes a boolean. The handler returns nothing on
 * purpose: Toggle treats a returned promise as an async save (optimistic +
 * pending), and Formik's setFieldValue returns one.
 * `ariaLabel` names a switch whose visible label sits outside it (Tax rate).
 */
const RCToggle = ({ label, name, ariaLabel }) => {
  const { values, setFieldValue } = useFormikContext();
  const on = !!fget(values, name);
  return (
    <Toggle
      label={label || undefined}
      aria-label={ariaLabel}
      checked={on}
      onChange={(next) => { setFieldValue(name, next); }}
    />
  );
};

// ─── Tabs + Accordion ─────────────────────────────────────────────────────────

// Status dot rendered next to a LISTING tab label.
//  - on      = green  → tab is "live" (DAO listing active, or toggle ON)
//  - off     = orange → toggle off, or brand-new listing not yet enabled
//  - pending = orange → Chrono24 only: in the feed, not yet seen live
//  - neutral = grey   → Chrono24 only: toggle off
//
// Chrono24 uses pending/neutral instead of on/off because orange there means
// "queued, not yet live" — leaving its off state orange would make one colour
// mean two opposite things on the same screen. Other channels keep on/off
// unchanged. `state` wins when given; `on` remains for every existing caller.
const DOT_STATES = ["on", "off", "pending", "neutral"];
const DOT_LABELS = {
  on: "Live",
  off: "Disabled",
  pending: "Sent to Chrono24, not yet live",
  neutral: "Disabled",
};
const TabStatusDot = ({ on, state, title }) => {
  const resolved = DOT_STATES.includes(state) ? state : on ? "on" : "off";
  return (
    <span
      className={`rc-lp__tab-dot rc-lp__tab-dot--${resolved}`}
      aria-label={DOT_LABELS[resolved]}
      title={title}
    />
  );
};

// Panel-switching sub-tabs → ui Tabs (underline: they sit inside a section
// card). `locked` is deliberately NOT forwarded: a plan-locked tab is still
// selectable here — selecting it renders LockedTabPanel (the upgrade path) —
// and its LockDot already marks it.
const RCTabs = ({ tabs, active, onChange, label = "Tabs" }) => (
  <Tabs
    className="rc-lp__tabs"
    variant="underline"
    aria-label={label}
    value={active}
    onChange={(key) => onChange(key)}
    items={tabs.map(({ key, label: text, dotOn, dotComponent }) => ({
      value: key,
      label: (
        <>
          {text}
          {/* Status dot is opt-in: a tab shows one only if it provides a
              `dotComponent` (LISTINGS tabs) or an explicit `dotOn`. Tabs
              that pass neither (Images, Cost, Purchase Order, Upload
              Invoice) render no dot. */}
          {dotComponent ?? (dotOn !== undefined ? <TabStatusDot on={!!dotOn} /> : null)}
        </>
      ),
    }))}
  />
);

/**
 * Chrono24 tab dot — LIVE publish status, not just the toggle.
 *
 * Handles both account shapes: the legacy DAO-UK tab (slug === null) reads
 * is_listed from listing.is_chrono, every other account reads it from its
 * junction row. Either way the publish_state that colours the dot comes from
 * the junction row, and the colour rules are the shared ones the listing card
 * uses — see helpers/chrono24PublishState.js.
 *
 * Fetched via react-query (cached by useListingMarketplaceState), so several
 * Chrono24 tabs on one form stay cheap.
 */
const Chrono24PublishDot = ({ listing, account }) => {
  const listingId = listing?.id;
  const isLegacy = account?.slug === null;
  const { state } = useListingMarketplaceState(listingId, account?.id, {
    enabled: !!listingId && !!account?.id,
  });

  const isListed = isLegacy ? !!listing?.is_chrono : !!state?.is_listed;
  const publish = ch24PublishPresentation({ account, isListed, state });

  return <TabStatusDot state={publish.dot} title={publish.pill?.title} />;
};

// Reads a flag straight off Formik values — used for tabs whose enable
// signal lives on the listing itself (is_chrono / is_ebay / is_wf).
// transform = optional predicate (e.g. status === 'active').
const FormikFlagDot = ({ name, transform }) => {
  const { values } = useFormikContext();
  const v = fget(values, name);
  return <TabStatusDot on={transform ? transform(v) : !!v} />;
};

// Dao Watches dot uses listing.status directly — the listing's status field
// isn't in ListingHelper.getInitialValues, so a FormikFlagDot on "status"
// would always be undefined → orange. Source of truth is the loaded listing.
const ListingActiveDot = ({ listing }) => (
  <TabStatusDot on={listing?.status === "active"} />
);

// Same gotcha for is_chrono / is_ebay / is_wf — these flags aren't in
// ListingHelper.getInitialValues either, so FormikFlagDot was always orange.
// Reads straight from the loaded listing prop. The form has no UI to toggle
// these (they're set from the listing card), so the static read is accurate.
const ListingFlagDot = ({ listing, flag }) => (
  <TabStatusDot on={!!listing?.[flag]} />
);

const RCSection = ({ icon, label, isOpen, onToggle, children }) => (
  <div className="rc-lp__section">
    <SectionTrigger icon={icon} label={label} isOpen={isOpen} onToggle={onToggle} />
    {isOpen && (
      <div className="rc-lp__section-body">
        {children}
        {/* Auto-save confirmation, edit flow only (context absent elsewhere).
            The accordion is single-open, so this sits under the section the
            rep is editing. */}
        <SectionSavedBadge />
      </div>
    )}
  </div>
);

// ─── Listings › Dao Watches ───────────────────────────────────────────────────

// Optional: where the store keeps this watch. Options are the store's own list
// (Owner Admin → Inventory Locations); the listing stores the option's id.
const InventoryLocationField = () => {
  const [options, setOptions] = useState([]);

  useEffect(() => {
    let alive = true;
    axios
      .get("/api/store/inventory-locations")
      .then(({ data }) => {
        if (!alive) return;
        setOptions([
          { id: "", name: "Not set" },
          ...(data?.locations || []).map((l) => ({ id: l.id, name: l.label })),
        ]);
      })
      .catch(() => {}); // optional field: on failure it just offers nothing
    return () => {
      alive = false;
    };
  }, []);

  return <RCSelect label="Inventory Location" name="inventory_location_id" options={options} />;
};

const DaoWatchesFields = ({ listing }) => {
  const { isMerchant, settings } = useContext(SettingsContext);
  const { user } = useContext(UserContext);

  return (
    <div className="rc-lp__fields-grid">
      <RCSelect label="Availability"        name="availability" options={DataHelper.availability} required />
      <RCMoney  label="Price on request"    name="price" currencyField="currency" />
      {isMulticurrency() && (
        <RCSelect label="Currency"          name="currency" options={currencyOptions()} />
      )}
      {/* The same list the dictation review panel offers. It was inline here,
          which is how the two could have started sending different ids. */}
      <RCSelect label="Condition"           name="condition" required options={conditionOptions} />
      <RCSelect label="Delivery Set"        name="box"          options={boxOptions} required />
      <RCSelect label="Manufacturing Year"  name="year"         options={yearOptions} />
      {/* Vintage pieces are dated by a production RANGE ("1952-1956") or a
          decade, not one year. Left empty — which it is for every listing
          that exists today — the watch is dated by the single year above. */}
      <RCSelect label="Manufacturing Year To" name="year_to"    options={yearOptions} />
      {/* CORRECT use of `is_dao_admin` (= `is_admin && !is_merchant`): the
          staff-vs-merchant distinction, not an RC-internal gate. "Owned By"
          writes the store's own `internal_product` field (label comes from
          `settings.name`), so tenant store admins are meant to set it —
          marketplace merchants are not. */}
      {user?.is_dao_admin && !isMerchant && (
        <RCSelect label="Owned By" name="internal_product" options={ownedByOptions(settings?.name)} />
      )}
      <RCInput label="Limited Edition" name="limited_number" placeholder="Number" />
      {user?.is_admin && !isMerchant && <InventoryLocationField />}
      <div className="rc-lp__field-full">
        <RCTextarea label="Description" name="description" placeholder="New Condition..." />
      </div>
    </div>
  );
};

// ─── Listings › Chrono 24 ─────────────────────────────────────────────────────

const ChronoFields = () => (
  <div className="rc-lp__fields-grid">
    <div className="rc-lp__field-full"><RCMoney label="Price on request" name="chrono_price" /></div>
    {/* Handling Time + Certified share one row (half-width). Certified was a
        bare hardcoded <certified>Yes</certified> in the feed — now a real
        per-account toggle (see chrono24_feed.blade.php / Chrono24FeedController). */}
    <RCNumber label="Handling Time (days)" name="chrono_handling_time" placeholder="Days" />
    <div className="rc-field">
      <div className="rc-field__label">&nbsp;</div>
      <RCToggle label="Chrono24 Certified" name="chrono_certified" />
    </div>
    <div className="rc-lp__field-full">
      <RCInput label="Product Name" name="chrono_product_name" placeholder="Product name" />
    </div>
    <div className="rc-lp__field-full">
      <RCTextarea label="Chrono24 Description" name="chrono_description" placeholder="Description..." />
    </div>
  </div>
);

// Per-account Chrono24 fields for non-legacy accounts (Chrono24 France, Dubai…).
// Visually identical to ChronoFields above (same RC* components, same grid).
// Values live under Formik `marketplace_states.acc{accountId}.{field}` — the
// `acc` prefix is deliberate: a numeric key here makes lodash's `set` build an
// array (so {2: {...}} becomes [undefined, undefined, {...}]), which then leaks
// bogus PUT requests for ids 0, 1, … on submit.
// Hydrates from the junction on mount for existing listings; new listings start blank.
const MarketplaceAccountFields = ({ account, listing }) => {
  const { values, setFieldValue } = useFormikContext();
  const namePrefix = `marketplace_states.acc${account.id}`;
  // Shared symbol table; unknown account currency → prefix its ISO code
  // rather than mislabelling it.
  const currencySymbol = currencyMeta(account.currency)?.symbol || `${account.currency} `;

  // Live Formik values, read at async-resolve time. The hydration GET below is
  // async; without this it closes over the values captured when the effect ran,
  // so a GET that resolves AFTER the rep typed would overwrite their input with
  // the stored (often null) value — the core of the CH24 Europe "price never
  // saves" bug. We read latestValues.current so we only fill fields still empty.
  const latestValues = useRef(values);
  latestValues.current = values;

  useEffect(() => {
    if (!account?.id) return;

    // CREATE flow (no listing.id yet) — no junction row exists, just seed
    // empty fields with the legacy chrono_* values so the tab mirrors what
    // AutoTitles populated on CH24 UK. Skip if Formik already holds values
    // (e.g. user typed into this tab already).
    if (!listing?.id) {
      const cur = latestValues.current;
      const fallback = (path, source) => {
        if (!fget(cur, path) && source) setFieldValue(path, source);
      };
      fallback(`${namePrefix}.product_name`, cur.chrono_product_name);
      fallback(`${namePrefix}.description`,  account.listing_description || cur.chrono_description);
      // Certified has no per-tab source to inherit from (unlike product
      // name/description) — every account defaults to certified=true,
      // matching the feed's prior hardcoded <certified>Yes</certified>.
      fallback(`${namePrefix}.certified`, true);
      return;
    }

    let cancelled = false;
    axios
      .get(`/api/listings/${listing.id}/marketplaces/${account.id}`)
      .then((res) => {
        if (cancelled) return;
        const d = res.data || {};
        const cur = latestValues.current;
        // Junction stores price as pence/cents (integer). Convert to major
        // units (e.g. 234500 → 2345) for display, matching the "Always
        // multiply before sending to API" convention.
        const priceMajor = d.price == null || d.price === "" ? "" : Number(d.price) / 100;
        // Description + product_name: fall back to the legacy chrono_* values
        // when the junction row is empty (e.g. first time editing a listing
        // that pre-existed before CH24 Europe was enabled).
        const incoming = {
          price:         priceMajor,
          handling_time: d.handling_time ?? "",
          product_name:  d.product_name || cur.chrono_product_name || "",
          description:   d.description  || account.listing_description || cur.chrono_description  || "",
          // Junction default (migration) is true, so an absent/false value
          // means "explicitly set to No" — never silently drop it to true.
          certified:     d.certified !== false,
        };
        // Only write fields the rep has NOT already filled, so a slow GET can
        // never wipe a value they just typed into this tab (bug 3).
        const toSet = ListingHelper.pickHydratedFields(
          fget(cur, namePrefix) || {},
          incoming
        );
        Object.entries(toSet).forEach(([k, v]) =>
          setFieldValue(`${namePrefix}.${k}`, v)
        );
      })
      .catch(() => { /* leave blank on error */ });
    return () => { cancelled = true; };
  }, [listing?.id, account?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="rc-lp__fields-grid">
      <div className="rc-lp__field-full">
        <RCMoney label={`Price on request (${account.currency})`} name={`${namePrefix}.price`} currencySymbol={currencySymbol} />
      </div>
      {/* Handling Time + Certified share one row (half-width) — certified is
          settable independently per Chrono24 account (CH24 UK vs Europe). */}
      <RCNumber label="Handling Time (days)" name={`${namePrefix}.handling_time`} placeholder="Days" />
      <div className="rc-field">
        <div className="rc-field__label">&nbsp;</div>
        <RCToggle label="Chrono24 Certified" name={`${namePrefix}.certified`} />
      </div>
      <div className="rc-lp__field-full">
        <RCInput label="Product Name" name={`${namePrefix}.product_name`} placeholder="Product name" />
      </div>
      <div className="rc-lp__field-full">
        <RCTextarea label={`${account.label} Description`} name={`${namePrefix}.description`} placeholder="Description..." />
      </div>
    </div>
  );
};

// ─── Listings › Ebay ─────────────────────────────────────────────────────────

const EbayFields = () => (
  <div className="rc-lp__fields-grid">
    <div className="rc-lp__field-full"><RCMoney label="Price [GBP]" name="ebay_price" /></div>
    <div className="rc-lp__field-full">
      <RCNumber label="Handling Time (days)" name="ebay_handling_time" placeholder="Days" />
    </div>
    <div className="rc-lp__field-full">
      {/* Required only while eBay is connected — exactly when this tab exists
          (getValidationSchema drops ebay_title otherwise). */}
      <RCInput label="Title" name="ebay_title" placeholder="eBay title" required />
    </div>
    <div className="rc-lp__field-full">
      <RCTextarea label="eBay Description" name="ebay_description" placeholder="Description..." />
    </div>
  </div>
);

// ─── Listings › Watch Finder ──────────────────────────────────────────────────

// Mirrors the Chrono24 / eBay field set: editable Price, Product Name
// (auto-filled by AutoTitles, identical to Chrono24), and an optional
// description (NOT auto-filled). Publishing is driven by the manual toggle,
// not by a listing URL.
//
// Category / Strap Type / Clasp Type are backed by the real WatchFinder
// category cache (~45k references) via WatchFinderCategoryController:
//   - on mount, GET .../watchfinder/mapping resolves the same
//     brand+reference match WatchFinderService uses at push time (or the
//     admin's saved override, if one exists) and seeds Category + the
//     Strap/Clasp option lists + their auto-picked defaults.
//   - the Category field is a live search (partial match across all ~45k
//     rows), not a fixed list — picking a different one re-fetches that
//     category's Strap/Clasp options and resets those two fields to match.
// All five fields (category id, strap/clasp codes, paperwork date, limited
// edition) are plain Formik fields, so they save via the normal listing
// submit and — since ListingObserver watches them — re-queue the WatchFinder
// feed like any other wf_* edit when the toggle is on.
const WatchFinderFields = ({ listing }) => {
  const { values, setFieldValue } = useFormikContext();
  const listingId = listing?.id;

  const [categoryOption, setCategoryOption] = useState(null); // {id, name} for the closed pill
  // The resolver matched nothing for this reference. WatchFinderService then
  // returns null and the caller SKIPS the offer — silently, so without this
  // the merchant never learns the listing did not reach WatchFinder. Common
  // for vintage, whose typed-in reference is in no WatchFinder category.
  const [categoryUnmatched, setCategoryUnmatched] = useState(false);
  const [strapOptions, setStrapOptions] = useState([]);
  const [claspOptions, setClaspOptions] = useState([]);

  // A brand-new (unsaved) listing has no product/reference yet to resolve
  // against — nothing to fetch until it has an id.
  useEffect(() => {
    if (!listingId) return;
    let cancelled = false;
    axios.get(getApiUrl(`/api/listings/${listingId}/watchfinder/mapping`))
      .then(({ data }) => {
        if (cancelled) return;
        setStrapOptions(data.strapOptions || []);
        setClaspOptions(data.claspOptions || []);
        setCategoryUnmatched(!data.category && !values.wf_category_id);
        if (data.category) {
          setCategoryOption(data.category);
          // Only seed the id if this listing has never had one saved — an
          // existing saved override (already in Formik via initial values)
          // always wins over a fresh auto-resolve.
          if (!values.wf_category_id) setFieldValue("wf_category_id", data.category.id);
        }
        if (!values.wf_bracelet_type_code && data.selectedStrap) {
          setFieldValue("wf_bracelet_type_code", data.selectedStrap);
        }
        if (!values.wf_clasp_type_code && data.selectedClasp) {
          setFieldValue("wf_clasp_type_code", data.selectedClasp);
        }
      })
      .catch(() => {}); // no mapping available — fields stay editable but empty
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId]);

  // Window.COUNTRIES is loaded once at boot and serves {id, name} only —
  // /api/countries does not expose alpha2Code — so Paper Origin holds our own
  // countries.id. Widening that endpoint is a prerequisite if WatchFinder
  // turns out to want an ISO code.
  const countryOptions = useMemo(
    () => [{ id: "", name: "—" }, ...(Window.COUNTRIES || []).map((c) => ({ id: c.id, name: c.name }))],
    []
  );

  const searchCategories = useCallback(async (q) => {
    const { data } = await axios.get(getApiUrl("/api/watchfinder/categories/search"), {
      params: { q, limit: 20 },
    });
    return data?.data || [];
  }, []);

  // Admin picked a different Category — refetch ITS Strap/Clasp options
  // (they're per-reference) and drop the old selection, since a code that
  // was valid for the previous reference may not exist on the new one.
  // Auto-pick the new list's only option, mirroring the backend's
  // single-option auto-pick so the two stay consistent.
  const onCategoryPick = useCallback((opt) => {
    setCategoryOption(opt);
    // Picking one is the whole fix — the saved override always beats the
    // auto-resolver at push time.
    setCategoryUnmatched(false);
    axios.get(getApiUrl(`/api/watchfinder/categories/${opt.id}/attributes`))
      .then(({ data }) => {
        const nextStrap = data.strapOptions || [];
        const nextClasp = data.claspOptions || [];
        setStrapOptions(nextStrap);
        setClaspOptions(nextClasp);
        setFieldValue("wf_bracelet_type_code", nextStrap.length === 1 ? nextStrap[0].code : null);
        setFieldValue("wf_clasp_type_code", nextClasp.length === 1 ? nextClasp[0].code : null);
      })
      .catch(() => {});
  }, [setFieldValue]);

  return (
    <div className="rc-lp__fields-grid">
      <RCMoney label="Price [GBP]" name="wf_price" />
      <WfPayoutField />
      <div className="rc-lp__field-full">
        <RCInput label="Product Name" name="wf_product_name" placeholder="Product name" />
      </div>
      <div className="rc-lp__field-full">
        <RCSearchSelect
          label="Category"
          name="wf_category_id"
          selectedOption={categoryOption}
          loadOptions={searchCategories}
          placeholder="Search Brand / Model / Reference..."
          onPick={onCategoryPick}
        />
        {categoryUnmatched && (
          <div className="rc-lp__field-note" role="status">
            No WatchFinder category matched this reference — pick one above, or
            this listing will not be sent to WatchFinder.
          </div>
        )}
      </div>
      {/* Strap Type + Clasp Type share one row (half-width). Options come
          from the SELECTED category above, not a fixed list. */}
      <RCSelect
        label="Strap Type"
        name="wf_bracelet_type_code"
        options={strapOptions.map((o) => ({ id: o.code, name: o.label }))}
      />
      <RCSelect
        label="Clasp Type"
        name="wf_clasp_type_code"
        options={claspOptions.map((o) => ({ id: o.code, name: o.label }))}
      />
      {/* Bracelet Size + Manufacturer Warranty Date share one row (half-width). */}
      <RCInput label="Bracelet Size (cm)" name="wf_bracelet_size" placeholder="e.g. 20" />
      <RCDate label="Manufacturer Warranty Date" name="wf_manufacturer_warranty_date" />
      {/* Paperwork Date + Limited Edition share one row (half-width). */}
      <RCDate label="Paperwork Date" name="wf_paperwork_date" />
      <div className="rc-field">
        <div className="rc-field__label">&nbsp;</div>
        <RCToggle label="Limited Edition" name="wf_limited_edition" />
      </div>
      {/* Paper Origin + Manufacturer's Warranty Length share one row
          (half-width). Both optional — captured and stored, not yet sent to
          WatchFinder (see PRODUCT_OFFER_COLUMNS in WatchFinderService). The
          leading blank option is how an optional RCSelect is cleared. */}
      <RCSelect label="Paper Origin" name="wf_papers_origin_country_id" options={countryOptions} />
      <RCNumber
        label="Manufacturer's Warranty Length (months)"
        name="wf_manufacturer_warranty_months"
        placeholder="e.g. 24"
      />
      <div className="rc-lp__field-full">
        <RCTextarea label="WF Description" name="wf_description" placeholder="Description..." />
      </div>
    </div>
  );
};

// ─── Images ───────────────────────────────────────────────────────────────────

// WatchFinder consumes our listing images BY POSITION — there is no per-image
// tag. The backend's WatchFinderService::imageSlots() maps index 0→wristShot,
// 1→mainImage (Front), 2→Side, 3→Back, 4→Case Back, 5→Box/Papers, 6→Lifestyle;
// anything past index 6 is not sent.
// KEEP IN SYNC with WatchFinderService::imageSlots() — order is the contract.
const WF_IMAGE_SLOTS = [
  "Wrist Shot",
  "Front",
  "Side",
  "Back",
  "Case Back",
  "Box / Papers",
  "Lifestyle",
];

// Target dimensions per slot, from the Marketplace Image Guidelines deck.
// 'fixed'/'square' slots have one correct WxH — the safe auto-fix there is
// CONTAIN-fit + white padding, never a crop (cropping risks cutting off part
// of the watch). 'minHeight' slots (Side/Back/Case Back/Lifestyle) are
// guideline-recommended at a fixed height with a flexible width — only the
// height is enforced, and the fix is a proportional resize (no distortion).
const WF_IMAGE_SPECS = [
  { kind: "fixed",     w: 1500, h: 1920 }, // Wrist Shot
  { kind: "square",    w: 1500, h: 1500 }, // Front — "Main" in the guidelines
  { kind: "minHeight", h: 1920 },          // Side
  { kind: "minHeight", h: 1920 },          // Back
  { kind: "minHeight", h: 1920 },          // Case Back — "Back-side" in the guidelines
  { kind: "fixed",     w: 1500, h: 1920 }, // Box / Papers
  { kind: "minHeight", h: 1920 },          // Lifestyle
];

const wfSpecText = (spec) =>
  spec.kind === "minHeight" ? `${spec.h}px tall · width flexible` : `${spec.w}×${spec.h}px`;

const wfSpecMismatch = (spec, w, h) => {
  if (!w || !h) return false;
  return spec.kind === "minHeight" ? h !== spec.h : (w !== spec.w || h !== spec.h);
};

// Loads an <img> for canvas use. Files are served same-origin (proxied via
// /api), so this never taints the canvas.
const wfLoadImageEl = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

// Auto-fix for a fixed/square target. The admin picks in WfFixChoiceModal:
//   "pad"  — contain-fit inside the box, remainder white (guidelines' white
//            background; never cuts off any of the watch)
//   "crop" — cover-fit, overflow cropped at `pos` (0..1 along the long axis)
const wfCanvasFixToBox = async (src, targetW, targetH, mode = "pad", pos = 0.5) => {
  const img = await wfLoadImageEl(src);
  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, targetW, targetH);
  const r = mode === "crop"
    ? wfCoverRect(img.naturalWidth, img.naturalHeight, targetW, targetH, pos)
    : wfFitAndPadRect(img.naturalWidth, img.naturalHeight, targetW, targetH);
  ctx.drawImage(img, r.x, r.y, r.w, r.h);
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
};

// Safe auto-fix for a minHeight target: proportional resize to the target
// height (width follows) — never distorts the aspect ratio.
const wfCanvasResizeToHeight = async (src, targetH) => {
  const img = await wfLoadImageEl(src);
  const { canvasW, canvasH } = wfResizeToHeightSize(img.naturalWidth, img.naturalHeight, targetH);
  const canvas = document.createElement("canvas");
  canvas.width = canvasW;
  canvas.height = canvasH;
  canvas.getContext("2d").drawImage(img, 0, 0, canvasW, canvasH);
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
};

/**
 * WatchFinder's own image picker — 7 fixed, independently-addressable boxes
 * (not an ordered list) because WatchFinder reads images BY POSITION: "Front"
 * always means slot 1, whether or not slots 0/2+ are filled. A plain ordered
 * gallery (GalleryUpload/RUG, used by every other marketplace tab) can't
 * represent a deliberately empty middle slot without every later image
 * shifting up — so this is a separate component, not a GalleryUpload variant.
 *
 * Data shape: `marketplace_images.wf` (once this tab is touched) is always
 * exactly 7 entries, each a file id or `null` for an empty box — see
 * ListingService::syncFilesByType (backend tolerates the nulls, preserving
 * position) and WatchFinderService::imageSlots() (reads by stored slot
 * position, not list position). Before the tab is touched it reads DAO's
 * plain list positionally (first 7, padded), matching the pre-existing
 * "untouched mirrors DAO" convention — any interaction here forks a copy
 * into marketplace_images.wf, same as every other marketplace tab.
 *
 * Uploaded/reused images land in an unassigned TRAY below the upload button
 * (session-only local state, not saved) and the admin drags them up into a
 * box. Removing a box's image (the × or dragging it back to the tray) empties
 * that box — it does NOT get backfilled by a later image.
 */
const WatchFinderImageSlots = ({ productId, readFieldName, writeFieldName, onUserEdit }) => {
  const { values, setFieldValue } = useFormikContext();

  const normalizeSlots = (arr) => {
    const a = (Array.isArray(arr) ? arr : []).slice(0, WF_IMAGE_SLOTS.length).map((v) => v ?? null);
    while (a.length < WF_IMAGE_SLOTS.length) a.push(null);
    return a;
  };
  const slots = normalizeSlots(fget(values, readFieldName));

  const [tray, setTray] = useState([]); // unassigned file ids — session-only
  const [dims, setDims] = useState({}); // fileId -> {w, h}, from onLoad
  const [fixingIndex, setFixingIndex] = useState(null);
  const [choosing, setChoosing] = useState(null); // slot index whose pad-vs-crop dialog is open
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const alert = useAlert();

  const commit = useCallback((nextSlots) => {
    setFieldValue(writeFieldName, nextSlots);
    onUserEdit?.();
  }, [writeFieldName, setFieldValue, onUserEdit]);

  const assignToSlot = useCallback((index, fileId) => {
    const next = [...slots];
    const prevIndex = next.indexOf(fileId);
    if (prevIndex !== -1) next[prevIndex] = null; // moving from another slot
    const bumped = next[index]; // whatever was here goes back to the tray, not lost
    next[index] = fileId;
    setTray((t) => {
      const withoutIncoming = t.filter((id) => id !== fileId);
      return bumped ? [...withoutIncoming, bumped] : withoutIncoming;
    });
    commit(next);
  }, [slots, commit]);

  const clearSlot = useCallback((index) => {
    const fileId = slots[index];
    if (!fileId) return;
    const next = [...slots];
    next[index] = null;
    setTray((t) => [...t, fileId]);
    commit(next);
  }, [slots, commit]);

  const onFilePick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const added = [];
      try {
        for (const f of files) {
          const fd = new FormData();
          fd.append("file", await shrinkImageForUpload(f));
          const { data } = await axios.post(getApiUrl("/api/files"), fd);
          if (data?.id) added.push(data.id);
        }
      } finally {
        // keep whatever uploaded before a failure
        setTray((t) => [...t, ...added]);
      }
    } catch (err) {
      alert.show("Upload failed. The image may be too large (max 5 MB) or the connection dropped — please try again.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const makeDragStart = (payload) => (e) => {
    e.dataTransfer.setData("text/plain", JSON.stringify(payload));
    e.dataTransfer.effectAllowed = "move";
  };
  const readDrop = (e) => {
    try { return JSON.parse(e.dataTransfer.getData("text/plain")); } catch { return null; }
  };
  const onSlotDrop = (index) => (e) => {
    e.preventDefault();
    const payload = readDrop(e);
    if (!payload?.fileId) return;
    if (payload.from === "slot" && payload.index === index) return;
    assignToSlot(index, payload.fileId);
  };
  const onTrayDrop = (e) => {
    e.preventDefault();
    const payload = readDrop(e);
    if (payload?.from === "slot") clearSlot(payload.index);
  };

  const recordDims = (fileId, w, h) =>
    setDims((d) => (d[fileId]?.w === w && d[fileId]?.h === h ? d : { ...d, [fileId]: { w, h } }));

  const fixSlot = async (index, mode = "pad", pos = 0.5) => {
    const fileId = slots[index];
    const spec = WF_IMAGE_SPECS[index];
    if (!fileId || !spec) return;
    setFixingIndex(index);
    try {
      const src = getFileUrl(fileId);
      const blob = spec.kind === "minHeight"
        ? await wfCanvasResizeToHeight(src, spec.h)
        : await wfCanvasFixToBox(src, spec.w, spec.h, mode, pos);
      if (!blob) return;
      const newId = await wfUploadBlob(blob, `wf-${WF_IMAGE_SLOTS[index].toLowerCase().replace(/\W+/g, "-")}.jpg`);
      if (newId) {
        const next = [...slots];
        next[index] = newId;
        commit(next);
      }
    } catch (err) {
      // leave the original image in place on failure
    } finally {
      setFixingIndex(null);
    }
  };

  return (
    <div className="wf-image-slots">
      <div className="wf-image-slots__grid">
        {WF_IMAGE_SLOTS.map((label, i) => {
          const fileId = slots[i];
          const spec = WF_IMAGE_SPECS[i];
          const d = fileId ? dims[fileId] : null;
          const mismatch = d && wfSpecMismatch(spec, d.w, d.h);
          const required = i < 3; // Wrist Shot / Front / Side — WatchFinder's mandatory core shots
          return (
            <div
              key={i}
              className={`wf-slot${fileId ? " wf-slot--filled" : ""}${required ? " wf-slot--required" : ""}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={onSlotDrop(i)}
            >
              <div className="wf-slot__header">
                <span className="wf-slot__label">{i + 1}. {label}</span>
                {required && <span className="wf-slot__badge">Required</span>}
              </div>
              {fileId ? (
                <div className="wf-slot__thumb" draggable onDragStart={makeDragStart({ from: "slot", index: i, fileId })}>
                  <img
                    src={getFileUrl(fileId)}
                    alt={label}
                    onLoad={(e) => recordDims(fileId, e.target.naturalWidth, e.target.naturalHeight)}
                  />
                  <IconButton
                    size="sm"
                    round
                    className="wf-slot__remove"
                    label={`Remove ${label}`}
                    data-btn="wf-image-delete"
                    onClick={() => clearSlot(i)}
                  >
                    <Icon name="X" size="sm" />
                  </IconButton>
                  {mismatch && (
                    <div className="wf-slot__warning">
                      <span>{d.w}×{d.h}px — needs {wfSpecText(spec)}</span>
                      <Button
                        variant="secondary"
                        size="sm"
                        data-btn="wf-image-edit"
                        // one fix at a time: fixSlot copies `slots` when it starts, so a
                        // second fix finishing later would write the first slot's
                        // original back (and auto-save would persist that)
                        disabled={fixingIndex !== null}
                        // height-only slots have one safe fix (resize); fixed/square
                        // slots ask: pad with white, or crop to fill
                        onClick={() => (spec.kind === "minHeight" ? fixSlot(i) : setChoosing(i))}
                      >
                        {fixingIndex === i ? "Fixing…" : "Auto-fix"}
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="wf-slot__empty">
                  <span className="wf-slot__empty-hint">Drop image here</span>
                  <span className="wf-slot__spec">{wfSpecText(spec)}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="rc-lp__image-drop">
        <div className="rc-lp__image-drop-icon"><IUpload /></div>
        <div className="rc-lp__image-drop-label">Drag and drop Images Here to Upload</div>
        <Button
          variant="secondary"
          data-btn="wf-image-add"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? "Uploading…" : "Select Images to Upload"}
        </Button>
        <input ref={fileInputRef} type="file" accept="image/*" multiple hidden onChange={onFilePick} />
      </div>

      <div className="wf-image-tray" onDragOver={(e) => e.preventDefault()} onDrop={onTrayDrop}>
        {tray.length === 0 ? (
          <div className="wf-image-tray__empty">
            Uploaded or reused images not yet placed in a box above appear here — drag one up to assign it.
          </div>
        ) : (
          tray.map((fileId) => (
            <div key={fileId} className="wf-image-tray__item" draggable onDragStart={makeDragStart({ from: "tray", fileId })}>
              <img src={getFileUrl(fileId)} alt="" />
              <IconButton
                size="sm"
                round
                className="wf-slot__remove"
                label="Remove from tray"
                data-btn="wf-tray-delete"
                onClick={() => setTray((t) => t.filter((id) => id !== fileId))}
              >
                <Icon name="X" size="sm" />
              </IconButton>
            </div>
          ))
        )}
      </div>

      <FolderList setImages={setTray} productId={productId} />

      {choosing !== null && slots[choosing] && (
        <WfFixChoiceModal
          label={WF_IMAGE_SLOTS[choosing]}
          src={getFileUrl(slots[choosing])}
          spec={WF_IMAGE_SPECS[choosing]}
          dims={dims[slots[choosing]]}
          onClose={() => setChoosing(null)}
          onChoose={(mode, pos) => {
            const index = choosing;
            setChoosing(null);
            fixSlot(index, mode, pos);
          }}
        />
      )}
    </div>
  );
};

// Per-marketplace images, rendered INSIDE each Listings tab below its
// fields. DAO (scope=null) is the master set, bound to `images`, with a
// Stock Image sub-tab. Every other tab ('chrono', 'chrono-{id}', 'ebay',
// 'wf') shows Listing Images only and behaves like this:
//   • UNTOUCHED  → READS DAO (`images`) so it shows DAO's images LIVE —
//     no copy, no effect; a DAO change is reflected immediately.
//   • on first EDIT → the change is WRITTEN to `marketplace_images.{scope}`
//     (forking a copy of DAO) and the scope is marked "touched".
//   • TOUCHED    → reads + writes its own `marketplace_images.{scope}`,
//     fully independent of DAO from then on.
const ImagesSection = ({ scope = null, showStock = true, active = true, listing, product, slotLabels = null, tabLabel = null }) => {
  const [tab, setTab] = useState(showStock ? "stock" : "listing");
  const { values, setFieldValue } = useFormikContext();
  // Prefer the live `product` prop (driven by RCProductSelector) over the
  // initial listing.product, which goes stale on the create flow.
  const activeProduct = product ?? listing?.product;
  const stockImages = activeProduct?.images ?? [];
  const productId = activeProduct?.id;

  const isTouched = scope ? (values._touched_scopes || []).includes(scope) : false;
  const scopeField = scope ? `marketplace_images.${scope}` : "images";
  // Untouched tab displays DAO live; touched tab (and DAO itself) uses its
  // own field. Edits always WRITE to the scope field, so editing an
  // untouched tab forks rather than mutating DAO.
  const readField = scope && !isTouched ? "images" : scopeField;
  const writeField = scopeField;

  const markTouched = () => {
    // DAO is the master — never "touched". And only the ACTIVE tab can be
    // touched: a hidden tab that remounts to reflect a DAO change must never
    // mark ITSELF customised (RUG fires onChange on that remount, which would
    // otherwise look like an edit). The user can only edit the visible tab.
    if (!scope || !active) return;
    const cur = values._touched_scopes || [];
    if (!cur.includes(scope)) {
      setFieldValue("_touched_scopes", [...cur, scope]);
    }
  };

  const TABS = showStock
    ? [
        { key: "stock",   label: "Stock Image" },
        { key: "listing", label: "Listing Images" },
      ]
    : [{ key: "listing", label: "Listing Images" }];

  return (
    <div className={`rc-lp__tab-images${active ? "" : " rc-lp__tab-images--hidden"}`}>
      {/* Named for the marketplace tab it sits in ("CH24 Europe Images"),
          the same way the per-tab Description fields are. */}
      <div className="rc-lp__images-heading">{tabLabel ? `${tabLabel} Images` : "Images"}</div>
      <RCTabs tabs={TABS} active={tab} onChange={setTab} label="Image sets" />
      {showStock && tab === "stock" && (
        !productId ? (
          <div className="rc-lp__image-drop">
            <div className="rc-lp__image-drop-label">
              Select a product above to see its stock images.
            </div>
          </div>
        ) : stockImages.length > 0 ? (
          <div className="rc-lp__stock-images">
            {stockImages.map((file) => {
              const url = getFileUrl(file.id);
              return (
                <div key={file.id} className="rc-lp__stock-image" onClick={() => window.open(url, "_blank")}>
                  <img src={url} alt="" />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rc-lp__image-drop">
            <div className="rc-lp__image-drop-icon"><IUpload /></div>
            <div className="rc-lp__image-drop-label">Drag and drop Images Here to Upload</div>
          </div>
        )
      )}
      {tab === "listing" && (
        productId ? (
          scope === "wf" ? (
            <WatchFinderImageSlots
              productId={productId}
              readFieldName={readField}
              writeFieldName={writeField}
              onUserEdit={markTouched}
            />
          ) : (
            <ProductImages
              readOnly={false}
              showTitle={false}
              productId={productId}
              readFieldName={readField}
              writeFieldName={writeField}
              onUserEdit={scope ? markTouched : undefined}
              slotLabels={slotLabels}
            />
          )
        ) : (
          <div className="rc-lp__image-drop">
            <div className="rc-lp__image-drop-label">
              Save the listing first — listing images unlock once a product is attached.
            </div>
          </div>
        )
      )}
    </div>
  );
};

// ─── Internal Details ─────────────────────────────────────────────────────────

/**
 * SELECT WATCHMAKER — the repair partner currently holding the watch.
 *
 * Deliberately the same shape AND the same search as the Supplier picker above
 * (async user search + a "new contact" button beside it), because it is the same
 * job: name a business contact that may not exist as a record yet.
 *
 * The search is deliberately NOT restricted to `type: 'watchmaker'` (Alex,
 * 2026-08-25): repairs already go to contacts filed as suppliers, merchants or
 * plain clients, and a type-filtered dropdown would have been empty for every
 * store on day one and would hide a partner who is already on file under another
 * type. So it searches EVERY user type, by person name, company name or store
 * name — exactly what `POST /api/users/search` does with no filters. NEW
 * WATCHMAKER is for a workshop that genuinely isn't on file yet.
 *
 * The one difference from the Supplier picker: this stores an ID
 * (`repair_watchmaker_id`, a real FK) rather than a free-text name, so the
 * reminder Pulses can quote the watchmaker's workshop and phone.
 *
 * The picker needs an option OBJECT to display, while Formik holds an id. The
 * label travels alongside the id in `_repair_watchmaker_label` (seeded by
 * ListingHelper.getInitialValues from the backend's `repair_watchmaker.label`),
 * so re-opening a saved listing shows the name immediately instead of a bare id
 * while an async lookup runs.
 */
const WatchmakerPicker = () => {
  const { values, setFieldValue } = useFormikContext();
  const [isCreating, setIsCreating] = useState(false);

  const id = fget(values, "repair_watchmaker_id") || "";
  const label = fget(values, "_repair_watchmaker_label") || "";

  // Memoised, NOT rebuilt inline. UserSearchSelect's value-sync effect is keyed
  // on `[value, defaultOptions]` and setStates on every run, so a fresh array
  // identity per render makes that effect fire on every render. The Supplier
  // picker in this same section avoids it by holding its options in useState;
  // this is the same guarantee with less machinery.
  const options = useMemo(
    () => (id ? [{ id, name: label || `Contact #${id}` }] : []),
    [id, label]
  );

  const select = (option) => {
    setFieldValue("repair_watchmaker_id", option?.id ?? "");
    setFieldValue("_repair_watchmaker_label", option?.name ?? "");
  };

  // Editing the watchmaker's record in the edit tab has to rewrite the label
  // held alongside the id, or the picker keeps showing the old workshop name.
  const onWatchmakerRefreshed = useCallback(
    (fresh) => {
      setFieldValue("_repair_watchmaker_label", userOptionName(fresh));
    },
    [setFieldValue]
  );
  useUserUpdates(id, onWatchmakerRefreshed, { refreshOnFocus: true });

  const onCreated = (user) => {
    if (user) {
      const name = [user.first_name, user.last_name].filter(Boolean).join(" ");
      // The pre-register response carries no `store` relation, so the store
      // suffix only appears once the record is re-read — matching the label
      // format when it is there, and degrading to the plain name when it isn't.
      const storeName = user.store?.name;
      select({ id: user.id, name: storeName ? `${name} | ${storeName}` : name });
    }
    setIsCreating(false);
  };

  return (
    <div className="rc-lp__field-full rc-lp__supplier-row">
      <div className="rc-field">
        <div className="rc-field__label">Select Watchmaker</div>
        <div className="rc-lp__supplier-controls">
          <Field
            name="repair_watchmaker_id"
            component={UserSearchSelect}
            className="select-container rc-supplier-select"
            placeholder="Search by name, company or store..."
            value={id || null}
            defaultOptions={options.length > 0 ? options : true}
            editable
            onChange={select}
          />
          <Button
            variant="secondary"
            size="lg"
            data-btn="watchmaker-new"
            onClick={() => setIsCreating(true)}
          >
            New Watchmaker
          </Button>
        </div>
        <div className="rc-lp__hint">
          Searches every contact by name, company or store. Use NEW WATCHMAKER
          for a workshop that isn’t on file yet — the type is pre-selected as
          Watchmaker and can be changed, and the store can be created inline.
        </div>
      </div>

      <SupplierWizardModal
        isOpen={isCreating}
        onClose={() => setIsCreating(false)}
        afterSubmit={onCreated}
        title="New Watchmaker"
        submitLabel="Create watchmaker"
        defaultType="watchmaker"
      />
    </div>
  );
};

/**
 * "In Repair" — the state the flows' CREATE REPAIR action puts a listing in —
 * and, once it is on, the details of the repair itself.
 *
 * Lives under Internal Details ▸ Cost because that is where the repair's costs
 * (polishing, shipping, travel) are already recorded, and because turning it
 * OFF is how a finished repair becomes an ordinary listing again.
 *
 * Backed by the real `listings.is_repair` column. Listings created before that
 * column existed instead carry an "[In Repair]" marker in the internal note,
 * and the listing card still reads that marker as a fallback — so switching the
 * toggle off has to clear BOTH, or a legacy repair would keep its badge after
 * being marked finished.
 *
 * The dates and the watchmaker render only while the toggle is ON, and are NOT
 * cleared when it goes off: a finished repair keeps its history, and the
 * reminder command gates on the flag, so a stale due date cannot pulse. None of
 * them is required — CREATE REPAIR still sets the flag alone.
 */
const InRepairToggle = () => {
  const { values, setFieldValue } = useFormikContext();
  const on = !!fget(values, "is_repair");
  const note = values?.private_note || "";
  const hasLegacyMarker = IN_REPAIR_MARKER_RE.test(note);
  IN_REPAIR_MARKER_RE.lastIndex = 0; // shared /g regex — reset between tests

  const toggle = () => {
    const next = !on;
    setFieldValue("is_repair", next);
    if (next) return;

    const cleaned = note.replace(IN_REPAIR_MARKER_RE, "").replace(/\s{2,}/g, " ").trim();
    IN_REPAIR_MARKER_RE.lastIndex = 0;
    if (cleaned !== note) setFieldValue("private_note", cleaned);
  };

  const age = repairAgeSummary({
    startedAt: fget(values, "repair_started_at"),
    expectedReturnAt: fget(values, "repair_expected_return_at"),
  });

  return (
    <>
      <div className="rc-lp__field-full">
        <Toggle label="In Repair" checked={on} onChange={() => toggle()} data-btn="listing-repair-toggle" />
        <div className="rc-lp__hint">
          {on
            ? "Shown as “In Repair” on the listing card. Turn off once the repair is done — it becomes a normal listing."
            : "Marks the watch as in for repair. The listing stays inactive and the card is labelled “In Repair”."}
          {on && hasLegacyMarker && " The “[In Repair]” note left by an older listing is removed too."}
        </div>
      </div>

      {on && (
        <>
          <RCDate label="Given For Repair" name="repair_started_at" />
          <RCDate label="Expected Return" name="repair_expected_return_at" />

          {age.text && (
            <div
              className={`rc-lp__field-full rc-lp__repair-age${
                age.overdue ? " rc-lp__repair-age--overdue" : ""
              }`}
            >
              {age.text}
            </div>
          )}

          <WatchmakerPicker />
        </>
      )}
    </>
  );
};

const InternalDetailsSection = ({
  listing,
  showDocs = true,
  supplierSlot = null,
  linkedRequestSlot = null,
}) => {
  const [tab, setTab] = useState("details");
  // Sub-tabs for the supporting documents below the Details fields.
  const [docTab, setDocTab] = useState("po");
  const { values } = useFormikContext();

  // Purchase Order PDF is resolved live (from the linked Sell Request)
  // by the backend onto listing.purchase_order_file_id. Invoices live in
  // Formik so the dot flips the instant a file is uploaded.
  const invoices = values?.invoices ?? [];
  const hasPurchaseOrder = !!listing?.purchase_order_file_id;
  // Green once a PO is shown or an invoice is uploaded; orange otherwise.
  const detailsDotOn = hasPurchaseOrder || invoices.length > 0;

  const DOC_TABS = [
    { key: "po",      label: "Purchase Order" },
    { key: "invoice", label: "Upload Invoice" },
  ];

  const CURRENCY_OPTIONS = [
    { id: "gbp", name: "GBP" }, { id: "eur", name: "EURO" },
    { id: "usd", name: "DOLLARS" }, { id: "hkd", name: "HKD" }, { id: "usdt", name: "USDT" },
  ];

  const TABS = [
    { key: "details", label: "Details",
      dotComponent: <TabStatusDot on={detailsDotOn} /> },
    { key: "cost",    label: "Cost" },
  ];

  return (
    <>
      <RCTabs tabs={TABS} active={tab} onChange={setTab} label="Internal details" />

      {tab === "details" && (
        <div className="rc-lp__fields-grid">
          <div className="rc-lp__field-full">
            <RCTextarea label="Internal Comment" name="private_note" placeholder="Internal notes..." />
            <div className="rc-lp__hint">Will not be Displayed</div>
          </div>
          <RCSelect label="Buying Currency" name="buying_currency" options={CURRENCY_OPTIONS} />
          <RCCurrencyInput label="Buying Price" name="buying_price" currencyField="buying_currency" />
          {/* The raw Supplier picker was removed 2026-09-04: the supplier is
              assigned in the ASSIGN SELLER / ASSIGN CONSIGNOR intake section
              (which writes `supplier`/`supplier_id` through the flow bridge)
              or carried over from the linked Sell Request. `supplier` still
              hydrates from the listing via FormHelper and still submits. */}
          {linkedRequestSlot && (
            <div className="rc-lp__field-full rc-lp__linked-row">{linkedRequestSlot}</div>
          )}
          {supplierSlot && (
            <div className="rc-lp__field-full rc-lp__supplier-row">{supplierSlot}</div>
          )}
          <RCInput label="Serial Number" name="serial_number" placeholder="Please input serial number..." />
          {/* Same field the sell and PX flows capture under VERIFY SERIAL, on the
              same listing column — so a watch verified in a request and one
              taken in as stock record their purchase date in one place. */}
          <RCDate label="Date of Purchase" name="date_purchase" />
          {/* Below Serial Number — the two things read off the watch and its
              papers. RCInput, not RCDate: a warranty is often quoted as just a
              year or a month, which a date picker cannot express. Using the
              form's own field component rather than a bespoke one is what keeps
              it identical to every other field in this section. */}
          <RCInput label="Warranty Date" name="warranty_date" placeholder={WARRANTY_DATE_PLACEHOLDER} />
          <RCDate label="Supplier Payment" name="supplier_payment_date" />
        </div>
      )}

      {tab === "details" && showDocs && (
        <div className="rc-lp__detail-docs">
          <RCTabs tabs={DOC_TABS} active={docTab} onChange={setDocTab} label="Supporting documents" />

          {docTab === "po" && (
            hasPurchaseOrder ? (
              <div className="rc-lp__detail-pdf">
                <PdfShower fileId={listing.purchase_order_file_url ?? listing.purchase_order_file_id} />
              </div>
            ) : (
              <div className="rc-lp__image-drop">
                <div className="rc-lp__image-drop-label">
                  No purchase order yet — it appears here once one is generated from the linked Sell Request.
                </div>
              </div>
            )
          )}

          {docTab === "invoice" && (
            <div className="rc-lp__detail-invoice">
              <InvoiceUpload name="invoices" />
            </div>
          )}
        </div>
      )}

      {tab === "cost" && (
        <div className="rc-lp__fields-grid">
          <div className="rc-lp__field-full">
            <RCSelect label="Cost Currency" name="costs_currency" options={CURRENCY_OPTIONS} />
          </div>
          <RCNumber label="Polishing"    name="polishing"    placeholder="Enter amount..." />
          <RCNumber label="Accessories"  name="accessories"  placeholder="Enter amount..." />
          <RCNumber label="Shipping"     name="shipping"     placeholder="Enter amount..." />
          <RCNumber label="Travel"       name="travel"       placeholder="Enter amount..." />
          <InRepairToggle />
        </div>
      )}
    </>
  );
};

// ─── Payment ──────────────────────────────────────────────────────────────────

const DepositRow = ({ index }) => {
  const n = index + 1;
  const base = `deposits[deposit_${n}]`;
  return (
    <div className="rc-lp__deposit">
      <div className="rc-lp__deposit-row">
        <span className="rc-lp__deposit-label">Deposit {n}</span>
        <div className="rc-lp__deposit-toggle-group">
          <RCToggle label="Transfer" name={`${base}[transfer]`} />
          <RCToggle label="Card"     name={`${base}[card]`} />
        </div>
        <span className="rc-lp__deposit-chevron"><IChevronRight /></span>
      </div>
      <div className="rc-lp__deposit-amount">
        <RCInput name={`${base}[amount]`} placeholder="Enter Amount..." />
      </div>
    </div>
  );
};

const PaymentSection = () => {
  const [tab, setTab] = useState("deposits");

  const TABS = [
    { key: "deposits",    label: "Deposits" },
    { key: "full_amount", label: "Full Amount" },
    { key: "tax_rate",    label: "Tax Rate" },
  ];

  return (
    <>
      <RCTabs tabs={TABS} active={tab} onChange={setTab} label="Payment" />
      {tab === "deposits" && (
        <>
          <DepositRow index={0} />
          <DepositRow index={1} />
          <DepositRow index={2} />
        </>
      )}
      {tab === "full_amount" && (
        <div className="rc-lp__full-amount-row">
          <RCToggle label="Activate card payments" name="allow_card_payments" />
        </div>
      )}
      {tab === "tax_rate" && (
        <div className="rc-lp__tax-row">
          <span>Margin scheme</span>
          <RCToggle label="" ariaLabel="VAT (off: margin scheme)" name="tax_rate" />
          <span>VAT</span>
        </div>
      )}
    </>
  );
};

// ─── RC Product List / RC Product Selector ───────────────────────────────────
// Extracted to src/product/components/RCProductSelector (Phase 1 of the Buy
// Alerts RC-theme redesign, see BUY_ALERTS_IMPLEMENTATION_PLAN.md). Lifted
// verbatim — no behaviour change — so this panel keeps working exactly as
// before; the shared component is now also reusable from the Buy Alerts
// create/edit flow. See RCProductSelector import above.

// ─── WatchFinder: payout + first-time price prefill ──────────────────────────
// The store's own listing price (the first tab), in GBP.
const useStorePriceGbp = () => {
  const { values } = useFormikContext();
  const { rates } = useAllFx("GBP");
  return storePriceGbp(values.price, values.currency || getBaseCurrency(), rates);
};

// "WF Payout": what WatchFinder pays out for the WatchFinder price — red when
// it is below the store's own listing price, green when not, no colour without
// both prices (Price on request = no store price).
export const WfPayoutField = () => {
  const { values } = useFormikContext();
  const store = useStorePriceGbp();
  const payout = wfPayout(values.wf_price);
  const tone = payout === null || payout <= 0 || store === null ? "" : payout < store ? " is-low" : " is-ok";
  const gbp = (v) => `£${Math.abs(Number(v)).toLocaleString("en-GB")}`;
  const wf = payout === null ? null : Math.round(Number(String(values.wf_price).replace(/[£€$₮,\s]/g, "")));
  // Commission is what is left over, so the line always adds up to the payout
  // to the pound (each part on its own would round separately).
  const commission = payout === null ? null : wf - WF_FEE_GBP - payout;
  const gap = tone ? payout - store : null;
  return (
    <div className="rc-field">
      <div className="rc-field__label">WF Payout</div>
      <output className={`rc-wf-payout${tone}`} aria-label="WF Payout">
        {payout === null ? "—" : gbp(payout)}
      </output>
      {payout === null ? (
        <div className="rc-wf-payout__note">
          {`WF price − ${(WF_COMMISSION * 100).toFixed(1)}% commission + VAT − ${gbp(WF_FEE_GBP)} WF fee`}
        </div>
      ) : (
        <>
          <div className="rc-wf-payout__note">
            {`${gbp(wf)} WF price − ${gbp(commission)} commission (${(WF_COMMISSION * 100).toFixed(1)}% + VAT) − ${gbp(WF_FEE_GBP)} WF fee`}
          </div>
          {gap !== null && (
            <div className={`rc-wf-payout__note rc-wf-payout__gap${tone}`}>
              {`${gbp(gap)} ${gap < 0 ? "below" : "above"} your store price ${gbp(store)}`}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// First time only: when the store price is entered on a listing that had none
// (a new listing, or one first priced now), fill the WatchFinder price so its
// payout matches the store price. Keeps following the store price while the rep
// is still typing it, stops the moment they edit the WatchFinder price
// themselves, and never runs when the store price was already set on load.
export const WfPricePrefill = ({ enabled }) => {
  const { values, setFieldValue } = useFormikContext();
  const store = useStorePriceGbp();
  const firstEntry = useRef(null);
  const lastAuto = useRef(null);

  if (firstEntry.current === null) {
    const loaded = Number(String(values.price ?? "").replace(/[£€$₮,\s]/g, ""));
    firstEntry.current = !(Number.isFinite(loaded) && loaded > 0);
  }

  useEffect(() => {
    if (!enabled || !firstEntry.current) return;
    const target = wfPriceForPayout(store);
    if (target === null) return;
    const current = Number(values.wf_price) || 0;
    const untouched = current === 0 || current === lastAuto.current;
    if (untouched && current !== target) {
      lastAuto.current = target;
      setFieldValue("wf_price", target);
    }
  }, [enabled, store]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

// ─── CH24 UK → other accounts FX price propagation (CREATE flow only) ───────
// When the rep enters a price on the CH24 UK tab during a brand-new listing
// creation, mirror it onto every non-legacy chrono24 account, converted via
// the same /api/fx service the Pricing tab uses. On the EDIT flow, this is a
// no-op so reps can hand-tune each region's price independently.
const ChronoUkPriceFxPropagator = ({ listing, chrono24Accounts }) => {
  const { values, setFieldValue } = useFormikContext();
  const { rates } = useAllFx("GBP");

  useEffect(() => {
    if (listing?.id) return;                        // EDIT mode — leave alone
    const ukPrice = Number(values.chrono_price);
    if (!Number.isFinite(ukPrice) || ukPrice <= 0) return;
    chrono24Accounts
      .filter((a) => a.slug !== null && a.currency && a.currency !== "GBP")
      .forEach((a) => {
        // rates[X] is X → GBP, so GBP → X divides (see helper).
        const converted = gbpToAccountPrice(ukPrice, rates, a.currency);
        if (converted === null) return;
        const path = `marketplace_states.acc${a.id}.price`;
        if (fget(values, path) !== converted) setFieldValue(path, converted);
      });
  }, [values.chrono_price, chrono24Accounts.length, rates]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

// ─── Auto-populate Chrono24 + eBay titles ────────────────────────────────────
const AutoTitles = ({ listing, chrono24Accounts = [] }) => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    let productName = null;
    try {
      productName = ProductHelper.getChronoProductName(values);
      setFieldValue("chrono_product_name", productName);
      // WatchFinder Product Name uses the same auto-fill as Chrono24.
      // (WF Description is intentionally left blank — not auto-populated.)
      setFieldValue("wf_product_name", productName);
    } catch (_) {} // eslint-disable-line no-empty
    try {
      setFieldValue("ebay_title", ProductHelper.getEbayProductName(values, listing));
    } catch (_) {} // eslint-disable-line no-empty

    // Propagate the chrono product name to every non-legacy account tab
    // (CH24 Europe, future regional accounts). Only when the per-account
    // field is empty — preserves any value the user has typed themselves.
    if (productName) {
      chrono24Accounts
        .filter((a) => a.slug !== null)
        .forEach((a) => {
          const path = `marketplace_states.acc${a.id}.product_name`;
          const current = fget(values, path);
          if (!current) setFieldValue(path, productName);
        });
    }
  }, [values.year, values.condition, values.product?.id, chrono24Accounts.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // The WatchFinder price is prefilled by WfPricePrefill (one formula, in
  // helpers/wfPayout.js) — not here.

  return null;
};

// Pre-fills the Chrono24 (legacy/UK) + eBay listing descriptions on a NEW
// listing from the store's OWN marketplace accounts' listing_description.
// Per-account regional Chrono24 tabs read their own account.listing_description
// in MarketplaceAccountFields. Only fills empty fields — never clobbers edits.
const MarketplaceDescriptionDefaults = ({ listing, chrono24Accounts = [], accounts = [] }) => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    if (listing?.id) return; // existing listing already has saved descriptions
    const legacyChrono = chrono24Accounts.find((a) => a.slug === null) || chrono24Accounts[0];
    const ebayAccount = accounts.find((a) => a.marketplace === "ebay");
    if (legacyChrono?.listing_description && !values.chrono_description) {
      setFieldValue("chrono_description", legacyChrono.listing_description);
    }
    if (ebayAccount?.listing_description && !values.ebay_description) {
      setFieldValue("ebay_description", ebayAccount.listing_description);
    }
  }, [listing?.id, chrono24Accounts.length, accounts.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

// ─── Flow prefill bridge ─────────────────────────────────────────────────────
// Purpose-built flows (New Stock / New Consignment) collect data OUTSIDE the
// form (uploaded invoice, selected Sell Request, AI-extracted fields) and hand
// it in as `flow.prefill`. This bridge applies each prefill onto Formik, but
// only into fields that are still empty — a value the rep already typed always
// wins.
//
// `initialValues` (Formik's own, from context) is in the deps and it is what
// makes this work: selecting a Sell Request also auto-picks its product, which
// changes the initialValues memo below, and `enableReinitialize: true` makes
// Formik reset the form to those values. Child effects run BEFORE the parent's,
// so a prefill applied in the same commit is wiped by that reset. Formik's reset
// swaps in a new initialValues object, so depending on its identity re-runs this
// effect right after — the prefill lands on the fresh form instead of under it.
// `enforce` is the second, smaller bucket: fields the flow DEFINES rather than
// suggests, written even over a non-empty value. It exists because
// FormHelper.initialValues hardcodes `internal_product: "owned"` for every new
// listing (helpers/Form.js), so that field is never empty and a soft prefill
// could never mark a listing as Consignment.
// `clear` is the third bucket and the only one that can write an EMPTY value:
// both others skip empties, by design (a prefill of "" would wipe a field the
// rep filled in). The edit page's intake section needs it — unassigning the
// seller there has to actually empty `supplier`, not silently leave the old name
// in the form.
const FlowPrefillBridge = ({ prefill, enforce, clear }) => {
  const { values, initialValues, setFieldValue } = useFormikContext();
  const latestValues = useRef(values);
  latestValues.current = values;

  useEffect(() => {
    const isEmpty = (v) =>
      v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);

    (clear || []).forEach((name) => {
      if (!isEmpty(fget(latestValues.current, name))) setFieldValue(name, "");
    });

    Object.entries(enforce || {}).forEach(([name, value]) => {
      if (isEmpty(value)) return;
      if (fget(latestValues.current, name) !== value) setFieldValue(name, value);
    });

    Object.entries(prefill || {}).forEach(([name, value]) => {
      if (isEmpty(value)) return;
      if (isEmpty(fget(latestValues.current, name))) setFieldValue(name, value);
    });
  }, [prefill, enforce, clear, initialValues]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

// ─── Flow value reporter ─────────────────────────────────────────────────────
// The reverse direction of FlowPrefillBridge: hands a few LIVE Formik values
// back out to the page that owns the intake sections. Those sections render
// above the form and outside Formik, so they cannot read it — but the edit
// page's first section depends on a field inside it: Owned By decides whether
// the store bought the watch (ASSIGN SELLER) or holds it on consignment
// (ASSIGN CONSIGNOR). Reading the SAVED value instead would go stale the moment
// the rep changes that dropdown.
//
// The effect depends on the joined values, not on `onReport`, so an inline
// arrow from the parent cannot loop it.
const FlowValueReporter = ({ fields, onReport }) => {
  const { values } = useFormikContext();
  const picked = fields.map((f) => fget(values, f));

  useEffect(() => {
    const out = {};
    fields.forEach((f, i) => { out[f] = picked[i]; });
    onReport(out);
  }, [picked.join(" ")]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

// ─── Marketplace-state reinit preserver ──────────────────────────────────────
// `enableReinitialize: true` resets the whole form whenever the memoized
// initialValues identity changes — picking or changing the PRODUCT does this by
// design, so product-derived fields re-seed. But `marketplace_states` is not
// part of initialValues (it belongs to the listing_marketplace_state junction,
// not the listings row), so every reinit silently wiped the per-account tabs:
// the CH24 Europe Certified toggle visibly flipped OFF the moment a product was
// selected, and an unsaved EUR price/description died with it. The per-tab
// seeding effect can't repair this — its deps ([listing?.id, account?.id])
// don't change on a reinit, so it never re-runs.
// Same ref-at-render idiom as latestValues in MarketplaceAccountFields: capture
// the live subtree BEFORE the reset lands (an effect would run after the reset
// render and capture the already-wiped undefined), restore it right after.
const MarketplaceStatePreserver = () => {
  const { values, initialValues, setFieldValue } = useFormikContext();
  const live = useRef(undefined);
  if (values.marketplace_states !== undefined) {
    live.current = values.marketplace_states;
  }

  useEffect(() => {
    if (live.current !== undefined && values.marketplace_states === undefined) {
      setFieldValue("marketplace_states", live.current);
    }
  }, [initialValues]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

// ─── Main form ────────────────────────────────────────────────────────────────

// `flow` (optional) turns the generic form into a purpose-built variant
// (New Stock / New Consignment) without touching the generic behaviour:
//   title                  — header title override
//   product                — externally auto-picked product (AI / Sell Request)
//   onProductChange        — (product|null) → void. The reverse: the product the
//                            rep picks in the SELECTOR, which is state owned by
//                            this component. A flow needs it to create the
//                            linked Sell Request (product_id is mandatory).
//   prefill                — soft field prefills (empty fields only)
//   enforce                — fields the flow defines (written over defaults),
//                            e.g. internal_product = owned / exclusive
//   clear                  — field names to EMPTY (the only bucket that can
//                            write an empty value; used when the edit page's
//                            intake section unassigns the seller)
//   topSections            — node rendered between header and product area
//                            (outside Formik — flow pages own that state)
//   openSectionRequest     — {key, nonce}: ask the form to open one of ITS OWN
//                            accordion sections (listings / internal /
//                            contracts / payment). The flow's intake sections
//                            and the form's sections are two separate
//                            accordions, so a flow that wants to hand over
//                            ("Sell Request picked → go straight to Listings")
//                            needs this. `nonce` must change per request so the
//                            same target can be re-opened later.
//   supplierSlot           — node replacing the Supplier picker inside
//                            Internal Details (the linked Sell Request card)
//   linkedRequestSlot      — node rendered ABOVE the Supplier picker, keeping
//                            it. What the EDIT page uses: the link and the
//                            supplier name are separate stored values.
//   reportFields + onReport — the reverse of prefill: field names whose LIVE
//                            Formik values are reported back to the page, for
//                            intake sections that render outside Formik but
//                            depend on a field inside it (Owned By).
//   renderContractsSection — ({openSection, toggleSection}) → node, after
//                            Internal Details (inside Formik)
//   renderStatusSection    — ({openSection, toggleSection, submitForm}) → node,
//                            after Payment (inside Formik); replaces the footer
// When flow is set, Internal Details hides its PO/Invoice doc tabs (they move
// to the flow's CONTRACTS section). The default footer buttons are hidden only
// when the flow supplies renderStatusSection — that section is what owns
// save/publish/repair. An EDIT overlay omits it and keeps the normal footer.
// `renderContracts` — ({openSection, toggleSection, listing}) → node. Supplied by
// the EDIT page so a saved listing shows the same CONTRACTS section the
// purpose-built flows use. Passed in rather than imported here on purpose:
// ContractsSection imports RCSection/RCTabs/InvoiceUpload back out of THIS file,
// so importing it would close an ESM cycle (this repo has already been bitten by
// one — see the vi.mock note in tests/setup.js).
const RCListingFormInner = ({ listing, afterSubmit, flow = null, renderContracts = null }) => {
  const { isMerchant, settings } = useContext(SettingsContext);
  const { user } = useContext(UserContext);
  const history = useHistory();
  const alert = useAlert();
  const queryClient = useQueryClient();

  // Deep link from a price check ("Edit price"): ?channel=chrono|ebay|wf on an
  // existing listing opens the Listings section on that sales-channel tab.
  const { search: locationSearch } = useLocation();
  const [deepLinkChannel] = useState(() => {
    const channel = new URLSearchParams(locationSearch || "").get("channel");
    return listing?.id && ["chrono", "ebay", "wf"].includes(channel) ? channel : null;
  });
  const [openSection, setOpenSection] = useState(deepLinkChannel ? "listings" : null);
  const [currentProduct, setCurrentProduct] = useState(listing?.product);
  // CONTRACTS replaces Internal Details' inline Purchase Order / Upload Invoice
  // tabs. Shown for the purpose-built flows AND wherever a caller supplies its
  // own (the edit page), so a listing reopens with the shape it was created in.
  const showsContracts = !!flow || !!renderContracts;

  // Flow auto-pick: an externally resolved product (Gemini match, or the
  // selected Sell Request's product) selects itself; the rep can still change
  // it via the selector afterwards.
  const flowProductId = flow?.product?.id;
  useEffect(() => {
    if (flowProductId && flowProductId !== currentProduct?.id) {
      setCurrentProduct(flow.product);
    }
  }, [flowProductId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ...and the reverse: the product the rep picks in the selector is state that
  // lives HERE, so a flow page otherwise cannot see it. It needs to — creating
  // the linked Sell Request requires a product_id, and sending the request
  // without one is a 422. Not part of `reportFields`, which reads Formik values:
  // `values.product` carries brand/model/reference but no id.
  useEffect(() => {
    flow?.onProductChange?.(currentProduct ?? null);
  }, [currentProduct?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [listingsTab, setListingsTab] = useState(deepLinkChannel || "store");
  // Tabs the user has opened. Each tab's image gallery is mounted on
  // first visit and then KEPT mounted (just hidden) so RUG keeps its own
  // order/state across tab switches — remounting on every switch dropped
  // unsaved reordering.
  const [visitedTabs, setVisitedTabs] = useState(() => new Set(["store"]));
  useEffect(() => {
    setVisitedTabs((prev) =>
      prev.has(listingsTab) ? prev : new Set(prev).add(listingsTab)
    );
  }, [listingsTab]);
  // Auto-save is on for EDITS only (the listing exists; every change persists
  // through the exact same PUT the Update button sends). Creation stays an
  // explicit action — a half-filled new listing must never save itself.
  const autoSaveOn = !!listing?.id;
  // "idle" | "saved" | "error" — feeds the per-section "Saved" badge.
  const [autoSaveState, setAutoSaveState] = useState({ phase: "idle", at: 0, message: null });
  const autoSaveCtx = useMemo(
    () => ({ enabled: autoSaveOn, ...autoSaveState }),
    [autoSaveOn, autoSaveState]
  );

  // Ref (not state) so the afterSubmit closure below reads the latest mode set
  // by the click handler without waiting for a React re-render. With auto-save
  // on, the RESTING mode is "auto": any submit not explicitly armed by a
  // button click is an auto-save and must neither navigate nor hand off to the
  // page's afterSubmit. The buttons arm "close"/"update" per click, and
  // wrappedAfterSubmit re-arms "auto" after every run.
  const saveModeRef = useRef(autoSaveOn ? "auto" : "update");
  const [repostConfirmOpen, setRepostConfirmOpen] = useState(false);

  const toggleSection = (key) =>
    setOpenSection((s) => (s === key ? null : key));

  // A flow handing over to one of the form's own sections. Keyed on the nonce so
  // re-requesting the same section works, and so a re-render alone can't
  // yank the accordion away from wherever the rep has navigated to.
  const openSectionNonce = flow?.openSectionRequest?.nonce;
  useEffect(() => {
    const key = flow?.openSectionRequest?.key;
    if (key) setOpenSection(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSectionNonce]);

  // Chrono24 is multi-account — one tab per enabled StoreMarketplaceAccount
  // for this listing's store (e.g. Chrono24 UK, Chrono24 France). The legacy
  // DAO-UK account (slug=null) renders with key="chrono" and the existing
  // ChronoFields component bound to flat listings.chrono_* columns. Other
  // accounts render with key="chrono-{accountId}" and a per-account form
  // bound to the listing_marketplace_state junction.
  // For a new listing there's no listing.store_id_m yet — fall back to the
  // ACTIVE store (Phase 5 store-switcher), then the logged-in user's home
  // store, via useActiveStoreIdM. A multi-store rep creating a new listing
  // while switched into a store other than their home one must see THAT
  // store's marketplace accounts, not their home store's. Only an RC master
  // with no store selected falls through to DAO UK; a user with no store gets
  // null and no accounts query at all, never DAO's tabs (L12).
  const fallbackStoreIdM = useActiveStoreIdM();
  const storeIdM = listing?.store_id_m || fallbackStoreIdM;
  const { accounts: chrono24Accounts, status: chrono24AccountsStatus } = useChrono24Accounts(storeIdM);
  // All marketplace accounts (same cached query) — used to gate the eBay and
  // Watch Finder tabs, exactly like the Chrono24 tabs: no enabled account for
  // this store → no tab, so a store can never publish through another
  // store's marketplace connection.
  const { accounts: allMarketplaceAccounts } = useMarketplaceAccounts(storeIdM);
  // Remove & Repost only makes sense for accounts the listing is switched ON
  // for — the rest are not in the feed, so there is no ad to remove.
  const repostAccounts = useListedChrono24Accounts(listing, chrono24Accounts);

  // Plan gating (Features 2+3): channels the store's plan doesn't include
  // render as LOCKED tabs with an upgrade path, never hidden (Alex,
  // 2026-08-13) — but only once accounts have loaded, and only for channels
  // with no enabled account (an existing account keeps its working tab).
  const { featureState } = useEntitlements();

  const listingIdForDots = listing?.id;
  const LISTING_TABS = useMemo(() => {
    const chronoTabs = chrono24Accounts.map((acc) => ({
      key:     acc.slug === null ? "chrono" : `chrono-${acc.id}`,
      label:   acc.label || "Chrono 24",
      account: acc,
      // One component for both account shapes: it shows live publish status
      // (green = seen on Chrono24, orange = queued), not just the toggle.
      dotComponent: <Chrono24PublishDot listing={listing} account={acc} />,
    }));

    // While accounts are still loading, keep the legacy single tab so the
    // form doesn't flicker. Once loaded, a store with NO enabled Chrono24
    // account gets no Chrono24 tab at all — the tab only appears when a
    // Marketplace Account exists for this store.
    const chronoFallback = chronoTabs.length === 0 && chrono24AccountsStatus !== "success"
      ? [{ key: "chrono", label: "Chrono 24", account: null,
          dotComponent: <ListingFlagDot listing={listing} flag="is_chrono" /> }]
      : chronoTabs;

    const hasAccount = (marketplace) =>
      allMarketplaceAccounts.some((a) => a.marketplace === marketplace && a.is_enabled);
    // Keep tabs visible while accounts load (same anti-flicker rule as the
    // Chrono24 fallback above).
    const loading = chrono24AccountsStatus !== "success";

    // Plan-locked tab for a channel with no enabled account: shown with a
    // lock, selecting it renders LockedTabPanel (upgrade request) instead of
    // form fields. 'hidden' (Future Release) channels stay absent entirely.
    const lockedTab = (marketplace, key, label, feature) =>
      !loading && !hasAccount(marketplace) && featureState(feature) === "locked"
        ? [{ key: `locked-${key}`, label, locked: true, feature,
            dotComponent: <LockDot /> }]
        : [];

    return [
      // First tab is the store's own listing — named after the store, never
      // a hardcoded brand.
      { key: "store",  label: settings?.name || "Store",
        dotComponent: <ListingActiveDot listing={listing} /> },
      ...chronoFallback,
      ...(chronoFallback.length === 0 ? lockedTab("chrono24", "chrono", "Chrono 24", "sync_chrono24") : []),
      ...(loading || hasAccount("ebay")
        ? [{ key: "ebay", label: "Ebay",
            dotComponent: <ListingFlagDot listing={listing} flag="is_ebay" /> }]
        : lockedTab("ebay", "ebay", "Ebay", "sync_ebay")),
      ...(loading || hasAccount("watchfinder")
        ? [{ key: "wf",   label: "Watch Finder",
            dotComponent: <ListingFlagDot listing={listing} flag="is_wf" /> }]
        : lockedTab("watchfinder", "wf", "Watch Finder", "sync_watchfinder")),
    ];
  }, [chrono24Accounts, chrono24AccountsStatus, allMarketplaceAccounts, settings?.name, listingIdForDots, listing?.status, listing?.is_chrono, listing?.is_ebay, listing?.is_wf, featureState]);

  let method = "post";
  let url = "/api/listings";

  // No success banner in this flow: AjaxForm renders `successTitle` as a green
  // Alert at the top of the form, but on save we immediately navigate to
  // /product/:id (see afterSubmit), so it only ever flashed. Omitting
  // successTitle suppresses it — error alerts are unaffected.
  if (listing?.id) {
    url += "/" + listing.id;
    method = "put";
  }

  // Captured during processSubmitValues so we can fire per-account PUTs after
  // the main listing save resolves with the (possibly new) listing id.
  const marketplaceStatesRef = useRef({});

  // Strip marketplace_states from the listing POST/PUT payload — those values
  // live on the listing_marketplace_state junction, not the listings row. They
  // get persisted in wrappedAfterSubmit once the main save resolves and we
  // know the listing id (matters for /product/create).
  const processSubmitValues = (values) => {
    const {
      marketplace_states,
      marketplace_images,
      _touched_scopes,
      ...listingValues
    } = values || {};
    marketplaceStatesRef.current = marketplace_states || {};

    // Only persist image sets for tabs the user actually customised (e.g. a
    // reorder on the CH24 Europe tab marks scope 'chrono-{id}' touched).
    // Untouched tabs are omitted so they keep following DAO via the backend's
    // per-scope DAO fallback (no rows of their own). Extracted + unit-tested
    // so the "rearranged images must save" guarantee is locked (bug 3).
    const scopedImages = ListingHelper.pickTouchedMarketplaceImages(
      marketplace_images,
      _touched_scopes
    );

    return {
      ...ListingHelper.processSubmitValues(listingValues),
      marketplace_images: scopedImages,
    };
  };

  // Wrap afterSubmit so the "Save & Close" button skips the preview hop
  // and returns the user to the Manage Listings page (audit item W). Also
  // persists per-account Chrono24 state (Chrono24 France, Dubai, …) via
  // the junction endpoint now that we know the listing id.
  const wrappedAfterSubmit = async (saved) => {
    // Read before the first await: the Remove & Repost confirm disarms
    // "repost" as soon as the submit settles (see its onConfirm).
    const mode = saveModeRef.current;
    const savedId = saved?.id || saved?.listing?.id || listing?.id;
    const states  = marketplaceStatesRef.current || {};
    let failedAccountIds = [];

    // The listing itself saved fine; the backend only publishes to eBay as a
    // non-blocking side-effect. If that push failed (e.g. eBay's counterfeit-
    // policy false-positive), it returns `ebay_sync_warning` instead of 500ing.
    // Surface it as a top-right toast so the rep knows eBay wasn't updated,
    // without blocking the save or losing the CH24 Europe price/images (bug 3).
    if (saved?.ebay_sync_warning) {
      alert.info(saved.ebay_sync_warning, { timeout: 8000 });
    }

    // Only PUT for keys we recognise (acc{id}) and only when the user actually
    // entered something. Skipping empty fields prevents the dual-write path
    // from wiping good listings.chrono_* values with nulls (issue 2 + 4).
    const entries = Object.entries(states)
      .map(([key, fields]) => {
        const m = String(key).match(/^acc(\d+)$/);
        if (!m || !fields) return null;
        const accountId = Number(m[1]);
        if (!Number.isFinite(accountId) || accountId <= 0) return null;
        const hasAny = [fields.price, fields.handling_time, fields.product_name, fields.description, fields.certified]
          .some((v) => v !== undefined && v !== null && v !== "");
        return hasAny ? [accountId, fields] : null;
      })
      .filter(Boolean);

    if (savedId && entries.length > 0) {
      const results = await Promise.allSettled(
        entries.map(([accountId, fields]) => {
          // Build a body of ONLY the fields the rep provided. The junction
          // endpoint validates with `sometimes`, so an omitted key is left
          // untouched — this is what stops an empty/auto-filled tab from
          // nulling a stored EUR price (bug 3). Price → pence inside the helper.
          const body = ListingHelper.buildMarketplaceStatePayload(fields);
          if (Object.keys(body).length === 0) return Promise.resolve({ accountId });
          return axios
            .put(`/api/listings/${savedId}/marketplaces/${accountId}`, body)
            .then(() => ({ accountId }))
            .catch((error) => Promise.reject({ accountId, error }));
        })
      );

      // Surface failures instead of swallowing them (the old allSettled hid a
      // failed EUR-price save entirely). No toast lib in this app yet, so log
      // to the console — visible in devtools / any error reporter that's wired.
      const failed = results
        .filter((r) => r.status === "rejected")
        .map((r) => r.reason?.accountId)
        .filter((id) => id != null);
      failedAccountIds = failed;
      if (failed.length > 0) {
        console.error(
          `Marketplace state save failed for account(s): ${failed.join(", ")} — retry the save.`
        );
      }
    }

    // The listing preview we navigate to next (/product/:id → views/Listing.jsx)
    // reads the listing through react-query: useListing(id) with the key
    // ["listing", id]. The app-wide default is staleTime 5 min /
    // refetchOnWindowFocus false (core/queryClient.js), so a listing cached
    // BEFORE this save is still considered fresh — the preview mounts straight
    // onto the pre-save copy and never refetches. That's why images uploaded
    // here only showed up in the carousel after a full page reload (which drops
    // the in-memory cache). Mark the cached listing stale so the preview
    // refetches on mount. Prefix key ["listing"] on purpose: the cache entry is
    // keyed by the raw route param (a string) while `savedId` is a number.
    queryClient.invalidateQueries({ queryKey: ["listing"] });

    if (autoSaveOn) saveModeRef.current = "auto"; // buttons re-arm per click

    if (mode === "close") {
      history.push("/rc/listings");
      return;
    }
    if (mode === "repost") {
      // Remove & Repost: the listing (and its per-account Chrono24 fields) is
      // saved above, so the new Chrono24 ad carries the current details. The
      // backend then gives the listing a new Chrono24 article id on every
      // account — the RC listing keeps its id.
      // A Chrono24 account whose details did not save would get a NEW ad built
      // from its OLD price/description, so cancel the repost instead.
      const failedChrono = chrono24Accounts.filter((a) => failedAccountIds.includes(Number(a.id)));
      if (failedChrono.length > 0) {
        alert.error(
          `Saved, but the Chrono24 details for ${failedChrono.map((a) => a.label).join(", ")} didn't save — repost cancelled, please try again.`,
          { timeout: 10000 }
        );
        return;
      }
      try {
        const { data } = await axios.post(`/api/listings/${savedId}/chrono24-repost`);
        const where = (data?.accounts || []).join(", ") || "Chrono24";
        alert.success(
          `Saved. Repost queued on ${where} (article ${data?.article_id}).`,
          { timeout: 10000 }
        );
        history.push("/rc/listings");
      } catch (error) {
        // The listing itself is saved; only the repost failed.
        showApiError(alert, error, "repost this listing to Chrono24");
      }
      return;
    }
    if (mode === "auto") {
      // Persisted quietly: flash the per-section "Saved" note, stay on the page.
      setAutoSaveState({ phase: "saved", at: Date.now(), message: null });
      return;
    }
    afterSubmit?.(saved);
  };

  // Memoize initialValues by listing identity + product identity. Without
  // this, every parent re-render builds a new object literal, and
  // `enableReinitialize: true` forces Formik to reset the form to the new
  // (identical-but-different-reference) values — which silently throws
  // away anything the user typed in non-active tabs (Chrono24, eBay,
  // Watch Finder). With memoisation, Formik only re-inits when the
  // underlying listing or selected product genuinely changes.
  // Flow-enforced fields (Owned By, In Repair) are baked INTO the reinit values,
  // read through a ref so a change to them cannot itself reinit the form.
  // Without this, a product auto-pick (selecting a Sell Request) reset
  // internal_product to FormHelper's hardcoded "owned" for one commit:
  // FlowValueReporter reported that transient value out to the page while
  // FlowPrefillBridge enforced the page's value back in, each one commit behind
  // the other — the Owned By dropdown flip-flopped between the two forever.
  const latestEnforce = useRef(null);
  latestEnforce.current = flow?.enforce || null;
  const initialValues = useMemo(() => {
    const base = ListingHelper.getInitialValues({ ...listing, product: currentProduct });
    Object.entries(latestEnforce.current || {}).forEach(([name, value]) => {
      if (value !== undefined && value !== null && value !== "") base[name] = value;
    });
    return base;
  }, [listing?.id, currentProduct?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // eBay Title only gates the save when the store actually has eBay connected
  // (same signal the eBay tab is gated on). Otherwise an over-length auto-title
  // blocks Edit Listing on a store that can't publish to eBay at all.
  const ebayEnabled = (allMarketplaceAccounts || []).some(
    (a) => a.marketplace === "ebay" && a.is_enabled
  );
  const validationSchema = useMemo(
    () => ListingHelper.getValidationSchema(listing, null, { ebayEnabled }),
    [listing?.id, ebayEnabled]
  );

  const formProps = {
    method, url,
    afterSubmit: wrappedAfterSubmit,
    initialValues,
    validationSchema,
    processSubmitValues,
    enableReinitialize: true,
    showFooter: false,
    excludeFields: ["product.query"],
    // Edit flow auto-saves every change (AjaxForm's built-in debounce →
    // validate → same full PUT). Failures land on the per-section badge.
    autoSave: autoSaveOn,
    autoSaveDebounce: 800,
    onAutoSaveError: (message) =>
      setAutoSaveState({ phase: "error", at: Date.now(), message }),
  };

  const refNumber   = currentProduct?.reference_number ?? "";
  const brandName   = currentProduct?.brand?.name ?? currentProduct?.brand_name ?? "";
  const modelName   = currentProduct?.name ?? currentProduct?.model?.name ?? currentProduct?.model_name ?? "";
  const listingYear = listing?.year ?? "";
  const imageUrl    = currentProduct?.images?.[0]
    ? getFileUrl(currentProduct.images[0].id)
    : currentProduct?.image_id > 0
      ? getFileUrl(currentProduct.image_id)
      : null;

  // Every listing flow (Edit Listing, New Listing, New Stock, New Consignment)
  // renders through here, so the standard page chrome lives here too rather than
  // in each flow page. The routes pass `noTopBar` to suppress RCAdminLayout's own
  // small title bar — without that, the flow shows its title twice.
  // See .claude/features/page-chrome.md for the PageLayout + PageHeader contract.
  const flowTitle = flow?.title ?? (listing?.id ? "Edit Listing" : "New Listing");

  return (
    <AutoSaveStatusContext.Provider value={autoSaveCtx}>
    <PageLayout className="rc-listing-flow">
      <PageHeader title={flowTitle} backTo="/rc/listings" compactOnScroll={false} />

      <div className="rc-listing-create">
        <div className="rc-lp">

        {/* Flow-specific intake sections (UPLOAD / ASSIGN SELLER / ASSIGN
            CONSIGNOR) — rendered ABOVE the product selector, outside Formik.
            Their data reaches the form via flow.product + flow.prefill. */}
        {flow?.topSections && (
          <div className="rc-lp__flow-top">{flow.topSections}</div>
        )}

        {/* Product area — selector + image card, outside AjaxForm */}
        <div className="rc-lp__product-area">
          <RCProductSelector
            product={currentProduct}
            onProductSelected={setCurrentProduct}
            // Purpose-built flows start on their own intake section (UPLOAD /
            // ASSIGN CONSIGNOR), and the product is usually resolved FROM that
            // choice — so the picker must not open itself and pull focus to a
            // step the rep hasn't reached. The generic create flow keeps the
            // default (open when nothing is selected), where picking the
            // product IS the first step.
            defaultOpen={flow ? false : undefined}
            openRequest={flow?.openProductRequest}
          />

          {/* Product card — only rendered when a product has been selected */}
          {currentProduct?.id && (
            <div className="rc-lp__product-card">
              <div className="rc-lp__product-meta">
                {refNumber  && <span>{refNumber}</span>}
                {refNumber  && brandName   && <span className="rc-lp__product-meta-dot">•</span>}
                {brandName  && <span>{brandName}</span>}
                {(refNumber || brandName) && listingYear && <span className="rc-lp__product-meta-dot">•</span>}
                {listingYear && <span>{listingYear}</span>}
              </div>
              {modelName && <div className="rc-lp__product-name">{modelName}</div>}
              <div className="rc-lp__product-image-area">
                {imageUrl ? (
                  <img src={imageUrl} alt={modelName} />
                ) : (
                  <div className="rc-lp__product-image-placeholder">
                    <IImage />
                    <span>No image</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Form */}
        <AjaxForm {...formProps}>
          {({ submitForm, resetForm }) => (
            <>
              <AutoTitles listing={listing} chrono24Accounts={chrono24Accounts} />
              <MarketplaceStatePreserver />
              <MarketplaceDescriptionDefaults listing={listing} chrono24Accounts={chrono24Accounts} accounts={allMarketplaceAccounts} />
              <ChronoUkPriceFxPropagator listing={listing} chrono24Accounts={chrono24Accounts} />
              <WfPricePrefill enabled={(allMarketplaceAccounts || []).some((a) => a.marketplace === "watchfinder" && a.is_enabled)} />
              {flow && (
                <FlowPrefillBridge prefill={flow.prefill} enforce={flow.enforce} clear={flow.clear} />
              )}
              {flow?.reportFields?.length > 0 && flow.onReport && (
                <FlowValueReporter fields={flow.reportFields} onReport={flow.onReport} />
              )}
              <div className="rc-lp__sections">

                <RCSection icon={<IList />} label="Listings"
                  isOpen={openSection === "listings"} onToggle={() => toggleSection("listings")}>
                  <RCTabs tabs={LISTING_TABS} active={listingsTab} onChange={setListingsTab} label="Sales channels" />
                  {listingsTab === "store"    && <DaoWatchesFields listing={listing} />}
                  {/* Price check bar — top of the CH24 UK (flat chrono_* fields),
                      other Chrono24 accounts (CH24 Europe…, converted from the
                      same check), eBay and WatchFinder tabs. Gated on
                      competitive_pricing_check inside; renders nothing on a
                      listing the checker never saw. */}
                  {(["chrono", "ebay", "wf"].includes(listingsTab) || listingsTab?.startsWith?.("chrono-")) && (
                    <PriceCheckTabBar key={listingsTab} listing={listing} tabKey={listingsTab} />
                  )}
                  {listingsTab === "chrono" && <ChronoFields />}
                  {listingsTab?.startsWith?.("chrono-") && (() => {
                    const tab = LISTING_TABS.find((t) => t.key === listingsTab);
                    return tab ? <MarketplaceAccountFields account={tab.account} listing={listing} /> : null;
                  })()}
                  {listingsTab === "ebay"   && <EbayFields />}
                  {listingsTab === "wf"     && <WatchFinderFields listing={listing} />}
                  {listingsTab?.startsWith?.("locked-") && (() => {
                    const tab = LISTING_TABS.find((t) => t.key === listingsTab);
                    return tab ? <LockedTabPanel label={tab.label} feature={tab.feature} /> : null;
                  })()}

                  {/* Images live INSIDE each marketplace tab, below its
                      fields. DAO shows Stock + Listing Images; every other
                      tab shows Listing Images only and keeps its own
                      independent set. Each visited tab's gallery stays
                      MOUNTED (hidden when inactive) so RUG preserves an
                      unsaved reorder across tab switches — only the active
                      one is shown. currentProduct drives the live preview. */}
                  {(user?.is_admin || user?.listing_creation_enabled) &&
                    LISTING_TABS.filter((t) => !t.locked && visitedTabs.has(t.key)).map((t) => (
                      <ImagesSection
                        key={t.key}
                        active={listingsTab === t.key}
                        scope={t.key === "store" ? null : t.key}
                        showStock={t.key === "store"}
                        listing={listing}
                        product={currentProduct}
                        slotLabels={t.key === "wf" ? WF_IMAGE_SLOTS : null}
                        tabLabel={t.label}
                      />
                    ))}
                </RCSection>

                <RCSection icon={<IDetails />} label="Internal Details"
                  isOpen={openSection === "internal"} onToggle={() => toggleSection("internal")}>
                  <InternalDetailsSection
                    listing={listing}
                    showDocs={!showsContracts}
                    supplierSlot={flow?.supplierSlot}
                    linkedRequestSlot={flow?.linkedRequestSlot}
                  />
                </RCSection>

                {/* CONTRACTS (PO / Invoices / Agreements) sits between Internal
                    Details and Payment. The purpose-built flows render their own
                    (they can also pull agreements from a linked Sell Request);
                    an EDIT gets the same section built from the saved listing, so
                    a listing created in New Stock / New Consignment reopens
                    looking like the flow that made it. */}
                {flow
                  ? flow.renderContractsSection?.({ openSection, toggleSection })
                  : renderContracts?.({ openSection, toggleSection, listing })}

                {!isMerchant && (
                  <RCSection icon={<IPayment />} label="Payment"
                    isOpen={openSection === "payment"} onToggle={() => toggleSection("payment")}>
                    <PaymentSection />
                  </RCSection>
                )}

                {/* Flow variants: LISTING STATUS carries the Save Draft /
                    Publish Listing / Create Repair actions. */}
                {flow?.renderStatusSection?.({ openSection, toggleSection, submitForm })}

              </div>

              {/*
                Footer buttons:
                  - Edit flow (listing.id present) shows BOTH "Save & Close"
                    (returns to /rc/listings) and "Update Product" (stays on
                    the listing preview).
                  - Create flow shows only "Create Listing".
                  - Audit asked for "Clear All" to be removed.
                  - Hidden only when a flow's LISTING STATUS section owns the
                    save actions (Save Draft / Publish / Create Repair). An edit
                    overlay uses the flow slots for its sections but has no
                    status section, so it keeps these buttons.
              */}
              {!flow?.renderStatusSection && (
              <div className="rc-lp__footer">
                <ButtonGroup align="end">
                  {listing?.id && listing?.status === "active" && repostAccounts.length > 0 && (
                    <Button
                      size="lg"
                      variant="secondary"
                      data-btn="listing-chrono24-repost"
                      onClick={() => setRepostConfirmOpen(true)}
                    >
                      Remove &amp; Repost
                    </Button>
                  )}
                  {listing?.id && (
                    <Button
                      size="lg"
                      variant="primary"
                      data-btn="listing-save-close"
                      onClick={() => { saveModeRef.current = "close"; submitForm(); }}
                    >
                      Save &amp; Close
                    </Button>
                  )}
                  <Button
                    size="lg"
                    variant={listing?.id ? "secondary" : "primary"}
                    data-btn={listing?.id ? "listing-save" : "listing-create"}
                    onClick={() => { saveModeRef.current = "update"; submitForm(); }}
                  >
                    {listing?.id ? "Update Product" : "Create Listing"}
                  </Button>
                </ButtonGroup>
                <ConfirmDialog
                  open={repostConfirmOpen}
                  onClose={() => setRepostConfirmOpen(false)}
                  onConfirm={() => {
                    setRepostConfirmOpen(false);
                    saveModeRef.current = "repost";
                    // Disarm once the submit settles, so a save that failed
                    // validation or the server can't leave "repost" armed for
                    // a later auto-save to fire unasked.
                    const disarm = () => {
                      if (saveModeRef.current === "repost") saveModeRef.current = autoSaveOn ? "auto" : "update";
                    };
                    Promise.resolve(submitForm()).then(disarm, disarm);
                  }}
                  title="Repost on Chrono24?"
                  description={`Saves your changes and replaces the Chrono24 ad on ${repostAccounts.map((a) => a.label).join(", ")} with a new one. The old ad's link, views and watchers are lost. Takes effect at Chrono24's next feed update.`}
                  confirmLabel="Remove & Repost"
                />
              </div>
              )}
            </>
          )}
        </AjaxForm>

        </div>
      </div>
    </PageLayout>
    </AutoSaveStatusContext.Provider>
  );
};

const RCListingForm = (props) => <RCListingFormInner {...props} />;

// ContractsSection reads these back out of this module, so they must stay
// exported (see the renderContracts note above for why the import goes one way).

// Shared building blocks for the purpose-built flow pages
// (src/listingFlows/*). Named exports only — the default export and the
// generic create/edit behaviour are unchanged.
export { RCSection, RCTabs, InvoiceUpload, TabStatusDot, RCInput, RCSelect, RCDate };

export default RCListingForm;
